import React, { useState, useCallback, useRef, useEffect, useMemo } from "react";

// =====================================================
// DATA — 10 FLOWS (F0–F9)
// =====================================================
const FLOWS = [
  { id:"F0",name:"People & Culture",engines:["Govern","All"],color:"#F97316",
    description:"Recruit → Bootcamp → Assign → Develop → Promote/Exit. Culture is the medium. People come first.",
    tools:[],
    steps:[
      {id:"f0s1",label:"Recruitment",desc:"Attract candidates aligned to REEF culture",status:"gap",note:"No recruitment tracking."},
      {id:"f0s2",label:"3-Week Bootcamp",desc:"Cultural filter: Compass, Codex, missions",status:"partial",note:"Ritual defined (draft)."},
      {id:"f0s3",label:"Engine & Mission Assignment",desc:"Officer assigned to engine and mission",status:"active",note:"OrgChart page tracks this."},
      {id:"f0s4",label:"Skills & Development",desc:"Skill progression, training, mentorship",status:"active",note:"SkillsManager built."},
      {id:"f0s5",label:"Rank Progression",desc:"Analyst → Operator → Commander",status:"active",note:"Gamification system tracks XP and rank."},
      {id:"f0s6",label:"Exit with Dignity",desc:"Graceful offboarding preserving relationships",status:"gap",note:"No exit workflow."},
    ]},
  { id:"F1",name:"Strategy & Execution",engines:["All","HC Governed"],color:"#059669",
    description:"The backbone — Purpose → Direction → Strategy → Tactics → Operations → Execution. Everything cascades from here.",
    tools:[],
    steps:[
      {id:"f1s1",label:"Purpose & Direction",desc:"High Council sets pillars and strategic direction",status:"active",note:"Codex page exists. Framework page shows structure."},
      {id:"f1s2",label:"Mission Definition",desc:"Missions encode Commander's Intent + Theater of War",status:"active",note:"14 missions active. Good commander's intent documentation."},
      {id:"f1s3",label:"Objective Discovery",desc:"AI-assisted SMART objective creation and validation",status:"active",note:"Discovery page with AI advisor. Objectives scored for quality."},
      {id:"f1s4",label:"Activity Planning",desc:"Activities decompose objectives with key results",status:"active",note:"44 activities. Gantt chart, principles validation built."},
      {id:"f1s5",label:"Daily Task Execution",desc:"Tasks logged, categorized, EP/XP allocated",status:"active",note:"327 daily tasks. Gamification layer working."},
      {id:"f1s6",label:"Progress Reporting",desc:"Officers report status with enrichment",status:"active",note:"116 progress reports. Issue tagging during reports."},
      {id:"f1s7",label:"Direction Adjustment",desc:"Pivots and priority shifts based on reviews",status:"gap",note:"No systematic feedback loop from reviews to direction changes."},
    ]},
  { id:"F2",name:"Content Acquisition",engines:["Promote","Build"],color:"#D4A017",
    description:"The proton funnel — content partners bring brand, recipes, IP into the Hexagon independently",
    tools:[{label:"Lighthouse",status:"gap"}],
    steps:[
      {id:"f2s1",label:"Content Discovery",desc:"Brand/kitchen identified or inbound",status:"gap",note:"No inbound content funnel exists. Discovery is ad hoc."},
      {id:"f2s2",label:"SAP-Offer Evaluation",desc:"Recipes, IP, brand strength assessed against Hexagon needs",status:"gap",note:"No scoring model or evaluation workflow in Compass."},
      {id:"f2s3",label:"Content Module Click",desc:"Content SAP-offer accepted → registered in Hexagon",status:"gap",note:"No Content module data model exists."},
      {id:"f2s4",label:"Content Readiness",desc:"Menu finalized, recipes standardized, packaging specs locked",status:"partial",note:"Handled operationally. No systematic checklist."},
      {id:"f2s5",label:"Proton Waiting",desc:"Content registered — waiting for OU match",status:"gap",note:"No queue/matching system."},
    ]},
  { id:"F3",name:"Operator & Unit Formation",engines:["Promote","Operate"],color:"#0D9488",
    description:"The neutron funnel — operator + ERE + capital assemble into an Operable Unit independently",
    tools:[{label:"JustCall",status:"active"}],
    steps:[
      {id:"f3s1",label:"Operator Attraction",desc:"Market Ulysses value prop",status:"gap",note:"No recruitment funnel tracked in Compass for Ulysses."},
      {id:"f3s2",label:"Operator Qualification",desc:"Assess readiness, fit, financial capacity",status:"gap",note:"No qualification workflow in app."},
      {id:"f3s3",label:"ERE Module Click",desc:"Real estate matched to operator profile",status:"gap",note:"No ERE module data model."},
      {id:"f3s4",label:"Capital Module Click",desc:"Funding matched to OU economics",status:"gap",note:"Capital flow exists but no module-level matching."},
      {id:"f3s5",label:"Operations Module Click",desc:"Operator assigned to space + capital structure",status:"partial",note:"Operator lifecycle partially in Compass."},
      {id:"f3s6",label:"Neutron Formed (OU)",desc:"Operator + ERE + Capital = Operable Unit",status:"partial",note:"OU concept exists but not tracked as event."},
    ]},
  { id:"F4",name:"Offering Nucleus Formed",engines:["Operate"],color:"#E04848",
    description:"Content meets OU → GTM clicks → all modules resolve → the offering nucleus has formed",
    tools:[],
    steps:[
      {id:"f4s1",label:"Content ↔ OU Match",desc:"Hexagon pairs content with operable unit",status:"partial",note:"Matching happens operationally."},
      {id:"f4s2",label:"GTM Module Click",desc:"Distribution channel selected",status:"gap",note:"No GTM module."},
      {id:"f4s3",label:"Hexagon Complete",desc:"All modules resolved — atom ready",status:"partial",note:"No formal 'atom complete' event."},
      {id:"f4s4",label:"Kickoff Ritual",desc:"Onboarding: T&Cs, deposit, checklist",status:"partial",note:"Ritual defined (draft) but execution_count: 0."},
    ]},
  { id:"F5",name:"Atom Formed & Sustained Reaction",engines:["Operate"],color:"#F97316",
    description:"Consumption clicks → the atom forms → orders flow → health monitoring → sustained energy",
    tools:[],
    steps:[
      {id:"f5s1",label:"Consumption Click",desc:"SAP-offer meets SAP-need — first orders",status:"external",note:"Transaction data in HexDB."},
      {id:"f5s2",label:"Order Cycle",desc:"Consumer demand → order → fulfillment → revenue",status:"external",note:"Managed through delivery platforms & HexDB."},
      {id:"f5s3",label:"Health Band Monitoring",desc:"Exploring → Struggling → Sustaining → Thriving",status:"partial",note:"Health bands defined. Weekly Health Check in draft."},
      {id:"f5s4",label:"Offering Optimization",desc:"Menu, pricing, availability adjusted",status:"gap",note:"No systematic optimization workflow."},
      {id:"f5s5",label:"Performance Escalation",desc:"Underperformance triggers issue detection",status:"partial",note:"No automated escalation."},
    ]},
  { id:"F6",name:"Financial Governance",engines:["Govern"],color:"#7C3AED",
    description:"Money flows through the organism: Sales → Collections → Cash → Payrun → Close",
    tools:[],
    steps:[
      {id:"f6s1",label:"Sales Recording",desc:"Gross sales captured per vessel per day",status:"active",note:"Captured in HexDB from POS."},
      {id:"f6s2",label:"Collections",desc:"Net sales → collections → cash in bank",status:"active",note:"Cash Conversion Dashboard exists."},
      {id:"f6s3",label:"Weekly Payrun",desc:"Operator payments processed weekly",status:"active",note:"Ritual institutionalized."},
      {id:"f6s4",label:"Expense Control",desc:"Spending authority policy enforcement",status:"partial",note:"Policy drafted. Not systematized."},
      {id:"f6s5",label:"Monthly Financial Close",desc:"Reconciliation, vessel P&L, risk flags",status:"partial",note:"Ritual in draft. execution_count: 0."},
      {id:"f6s6",label:"Investor Reporting",desc:"Data room accuracy, investor-ready financials",status:"gap",note:"No systematic investor reporting."},
    ]},
  { id:"F8",name:"DNA Triangle",engines:["Govern","All"],color:"#DC2626",
    description:"The organizational intelligence engine — issues gated, resolved, compounding every cycle",
    tools:[{label:"133 Issues",status:"critical"},{label:"24 Policies",status:"partial"},{label:"4 Capabilities",status:"gap"}],
    steps:[
      {id:"f8s1",label:"Issue Detection",desc:"Issues surface from Hexagon and Execution",status:"active",note:"133 issues captured."},
      {id:"f8s2",label:"Gate 1: Noise vs Signal",desc:"Is this real friction or an anomaly?",status:"critical",note:"BOTTLENECK: 128/133 stuck."},
      {id:"f8s3",label:"Gate 2: Balloon vs Rock",desc:"Density assessment",status:"critical",note:"Only 2 balloons, 2 rocks classified."},
      {id:"f8s4",label:"Balloon Resolution",desc:"Single-track operational adjustment",status:"gap",note:"No balloons resolved."},
      {id:"f8s5",label:"Rock Track 1: Policy",desc:"Immediate guardrails deployed",status:"critical",note:"0 issues linked to policies."},
      {id:"f8s6",label:"Rock Track 2: Capability",desc:"Formal mission to address gap",status:"critical",note:"0 linked to capabilities."},
      {id:"f8s7",label:"Rock Track 3: Operational",desc:"Fight with what you've got",status:"critical",note:"All not_started."},
    ]},
  { id:"F9",name:"Technology & Data Platform",engines:["Build"],color:"#2563EB",
    description:"Build → ship → operate the digital infrastructure",
    tools:[{label:"Compass",status:"active"},{label:"HexDB",status:"active"},{label:"Lighthouse",status:"critical"},{label:"NBRHD",status:"gap"}],
    steps:[
      {id:"f9s1",label:"Compass App",desc:"React app for organizational management",status:"active",note:"Core app running. 50+ pages."},
      {id:"f9s2",label:"HexDB / Sentinel",desc:"PostgreSQL data warehouse + AI query",status:"active",note:"85%+ accuracy on SQL queries."},
      {id:"f9s3",label:"AI Council",desc:"SOP Master, Bot Tasks, Memory",status:"partial",note:"SOP Master built. Bot Tasks prototype."},
      {id:"f9s4",label:"Lighthouse Platform",desc:"Universal navigator for Hexagon",status:"critical",note:"STUB: 512-byte placeholder."},
      {id:"f9s5",label:"Hexagon Data Model",desc:"Six-module marketplace with SAP matching",status:"gap",note:"No data model for modules."},
    ]},
];

const SC = {
  active:   {bg:"#F0FDF4",border:"#22C55E",text:"#166534",label:"Active"},
  partial:  {bg:"#FFFBEB",border:"#F59E0B",text:"#92400E",label:"Partial"},
  gap:      {bg:"#FFF7ED",border:"#F97316",text:"#9A3412",label:"Gap"},
  critical: {bg:"#FEF2F2",border:"#EF4444",text:"#991B1B",label:"Critical"},
  external: {bg:"#EFF6FF",border:"#3B82F6",text:"#1E40AF",label:"External"},
};

const HEXMODS = [
  {label:"Consumption",color:"#94A3B8",a:270},{label:"Capital",color:"#8B5CF6",a:330},
  {label:"ERE",color:"#3B82F6",a:30},{label:"Operations",color:"#0D9488",a:90},
  {label:"GTM",color:"#D97706",a:150},{label:"Content",color:"#D4A017",a:210},
];

const FPOS = {
  F0: { x: 50, y: 88 },
  F1: { x: 920, y: 220 },
  F6: { x: 380, y: 78 },
  F2: { x: 225, y: 340 },
  F5: { x: 670, y: 250 },
  F3: { x: 670, y: 390 },
  F4: { x: 670, y: 520 },
  F8: { x: 22, y: 560 },
  F9: { x: 480, y: 840 },
};

const SPINE = [
  {id:"sp_purpose",label:"Purpose",sub:"anchor",main:true},
  {id:"sp_direction",label:"Direction",sub:"pillars",main:true},
  {id:"sp_strategy",label:"Strategy",sub:"where we play",main:true},
  {id:"sp_tactics",label:"Tactics",sub:"how we win",main:true},
  {id:"sp_operations",label:"Operations",sub:"who does what when",main:true},
  {id:"sp_mission",label:"Mission",sub:"mobilize",indent:true},
  {id:"sp_objective",label:"Objective",sub:"discover",indent:true},
  {id:"sp_activity",label:"Activity",sub:"coordinate",indent:true},
  {id:"sp_execution",label:"Execution",sub:"perform daily",main:true},
  {id:"sp_task",label:"Task",sub:"perform",indent:true},
];
const SPINE_PAIRS = [
  ["sp_purpose","sp_direction"],["sp_direction","sp_strategy"],
  ["sp_strategy","sp_tactics"],["sp_tactics","sp_operations"],
  ["sp_operations","sp_mission"],["sp_mission","sp_objective"],
  ["sp_objective","sp_activity"],["sp_activity","sp_execution"],
  ["sp_execution","sp_task"],
];

const ALL_IDS = ["hexagon","F0","F1","F2","F3","F4","F5","F6","F8","F9"];
const FLOW_LABELS = {F0:"sustains",F1:"directs",F2:"content",F3:"OU forms",F4:"assembles",F5:"orders",F6:"funds",F8:"governs",F9:"enables"};

const PEOPLE_SPINE = [
  {id:"ps_codex",label:"Codex",sub:"foundation",main:true},
  {id:"ps_values",label:"Values",sub:"what we stand for",indent:true},
  {id:"ps_principles",label:"Principles",sub:"how we behave",indent:true},
  {id:"ps_redlines",label:"Red Lines",sub:"what we never do",indent:true},
  {id:"ps_orgchart",label:"Org Chart",sub:"structure",main:true},
  {id:"ps_engines",label:"Engines",sub:"functional groups",indent:true},
  {id:"ps_hierarchy",label:"Hierarchy",sub:"reporting lines",indent:true},
  {id:"ps_ranks",label:"Ranks & Roles",sub:"levels & duties",indent:true},
  {id:"ps_profiles",label:"Team Profiles",sub:"identity",main:true},
  {id:"ps_drive",label:"Drive",sub:"motivation",indent:true},
  {id:"ps_usermanual",label:"User Manual",sub:"work style",indent:true},
  {id:"ps_aihuman",label:"AI/Human",sub:"augmentation",indent:true},
];
const PEOPLE_PAIRS = [
  ["ps_codex","ps_values"],["ps_values","ps_principles"],["ps_principles","ps_redlines"],
  ["ps_redlines","ps_orgchart"],["ps_orgchart","ps_engines"],["ps_engines","ps_hierarchy"],
  ["ps_hierarchy","ps_ranks"],["ps_ranks","ps_profiles"],["ps_profiles","ps_drive"],
  ["ps_drive","ps_usermanual"],["ps_usermanual","ps_aihuman"],
];

// =====================================================
// 14 CINEMATIC SCENES
// =====================================================
const SCENES = [
  { id:0, title:"It starts with People", sub:"Before strategy. Before systems. People. Culture is the medium through which everything flows.",
    color:"#F97316", dur:8000,
    show:["F0"],
    showPeopleSpine: true,
    arcs:[{f:"F0",t:"F1",c:"#F97316",l:"enables",off:-60}],
    timeline: t => {
      const gap = 500;
      for (let i = 0; i < PEOPLE_SPINE.length; i++) {
        t.s(() => t.setC(p => ({...p, peopleSpineRevealed: i + 1})), 300 + i * gap);
      }
    }},

  { id:1, title:"The P2E Backbone Unfolds", sub:"Strategy & Execution forms. Purpose cascades — each layer creating the next — all the way down to daily tasks.",
    color:"#059669", dur:15000,
    show:["F0","F1"],
    arcs:[{f:"F0",t:"F1",c:"#F97316",l:"enables",off:-60}],
    particles:[{f:"F0",t:"F1",c:"#F97316",del:0.3,off:-60}],
    timeline: t => {
      const gap = 1200;
      for (let i = 0; i < SPINE.length; i++) {
        if (i > 0) t.s(() => t.setC(p => ({...p, spineParticle: i - 1})), 500 + i * gap - 400);
        t.s(() => t.setC(p => ({...p, spineRevealed: i + 1, spineParticle: -1})), 500 + i * gap);
      }
      t.s(() => t.setC(p => ({...p, spineParticle: -1})), 500 + SPINE.length * gap);
    }},

  { id:2, title:"Direction Creates the Marketplace", sub:"From Direction, the Hexagon is born. Six modules form one by one — the marketplace takes shape.",
    color:"#D4A017", dur:13000,
    show:["F0","F1","hexagon"],
    timeline: t => {
      t.s(() => t.setC(p => ({...p, directionGlow: true})), 500);
      t.s(() => t.setC(p => ({...p, directionArc: true})), 1500);
      t.s(() => t.setC(p => ({...p, hexVisible: true})), 3000);
      const modGap = 1200;
      for (let i = 0; i < 6; i++) {
        t.s(() => t.setC(p => ({...p, hexModsRevealed: i + 1})), 4000 + i * modGap);
      }
      t.s(() => t.setC(p => ({...p, directionGlow: false, directionArc: false})), 12000);
    }},

  { id:3, title:"A Brand Knocks", sub:"Brand shows interest and enters the proton funnel. Content is evaluated for a fit. The Content module clicks blue.",
    color:"#D4A017", dur:7000,
    show:["F0","F1","hexagon","F2"],
    arcs:[{f:"F2",t:"hexagon",c:"#D4A017",l:"content"}],
    particles:[{f:"F2",t:"hexagon",c:"#D4A017",del:0.3}],
    timeline: t => { t.flash3("Content","#3B82F6", 2500); }},

  { id:4, title:"Needs Surface", sub:"Content locked in. But ERE, Capital, Operations now show needs. The proton waits for a neutron.",
    color:"#D4A017", dur:6000,
    show:["F0","F1","hexagon","F2"],
    timeline: t => {
      t.s(() => t.hexG(true), 0); t.s(() => t.hexG(false), 2000);
      t.flash3("ERE","#DC2626", 1500); t.flash3("Capital","#DC2626", 1900); t.flash3("Operations","#DC2626", 2300);
    }},

  { id:5, title:"An Operator Steps Up", sub:"Ulysses enters the neutron funnel. Qualified. ERE, Capital, Operations click blue. The Operable Unit (OU) is formed.",
    color:"#0D9488", dur:8000,
    show:["F0","F1","hexagon","F2","F3"],
    arcs:[{f:"F3",t:"hexagon",c:"#0D9488",l:"OU forms"}],
    particles:[{f:"F3",t:"hexagon",c:"#0D9488",del:0.3}],
    timeline: t => {
      t.s(() => t.hexG(true), 2000); t.s(() => t.hexG(false), 4000);
      t.flash3("Operations","#3B82F6", 3500); t.flash3("ERE","#3B82F6", 3900); t.flash3("Capital","#3B82F6", 4300);
    }},

  { id:6, title:"The Offering Assembles", sub:"Content meets OU in F4. GTM clicks — distribution matched. We now have a positive charge.",
    color:"#E04848", dur:10000,
    show:["F0","F1","hexagon","F2","F3","F4"],
    arcs:[{f:"F3",t:"F4",c:"#0D9488",l:"OU"},{f:"F4",t:"hexagon",c:"#E04848",l:"assembles"}],
    particles:[{f:"F3",t:"F4",c:"#0D9488",del:0.3},{f:"F4",t:"hexagon",c:"#E04848",del:2.2}],
    timeline: t => {
      t.s(() => t.hexG(true), 3000); t.s(() => t.hexG(false), 5000);
      t.flash3("GTM","#3B82F6", 5000);
    }},

  { id:7, title:"Sustained Reaction, Atom Forms", sub:"Consumption clicks. SAP-offer meets SAP-need. Orders stream. The Hexagon electrifies. Atom is formed",
    color:"#F97316", dur:11000,
    show:["F0","F1","hexagon","F2","F3","F4","F5"],
    arcs:[{f:"F5",t:"hexConsumption",c:"#F97316",l:"orders",off:-40}],
    particles:[{f:"F5",t:"hexConsumption",c:"#F97316",del:0.3,off:-40},{f:"F5",t:"hexConsumption",c:"#F97316",del:1.2,off:-40},{f:"F5",t:"hexConsumption",c:"#F97316",del:2.0,off:-40}],
    timeline: t => {
      t.flash3("Consumption","#3B82F6", 2500);
      t.s(() => t.setC(p => ({...p, powerup:1})), 4000);
      t.s(() => t.setC(p => ({...p, powerup:2})), 4400);
      t.s(() => t.setC(p => ({...p, powerup:3})), 4900);
      t.s(() => t.setC(p => ({...p, powerup:4})), 5600);
      t.s(() => t.setC(p => ({...p, hexBorder:0.01})), 5900);
      let prog = 0;
      const bi = setInterval(() => {
        prog += 0.03; if (prog >= 1) { clearInterval(bi); t.setC(p => ({...p, hexBorder:1, hexBorderGlow:true})); return; }
        t.setC(p => ({...p, hexBorder:prog}));
      }, 40);
      t.T.push(bi);
      t.s(() => t.setC(p => ({...p, powerup:5})), 8500);
      t.s(() => t.setC(p => ({...p, hexBorderGlow:false, hexBorder:0, powerup:0})), 10000);
    }},

  { id:8, title:"The Business Cell", sub:"The membrane seals. Content, GTM, OU, Consumption, Funnels and continuous Reaction — this is the business cell. Now the organism must sustain it and build with it.",
    color:"#475569", dur:8000,
    show:["F0","F1","hexagon","F2","F3","F4","F5"],
    timeline: t => {
      t.s(() => t.setC(p => ({...p, membraneVisible:true})), 500);
      let prog = 0;
      t.s(() => {
        const mi = setInterval(() => {
          prog += 0.02; if (prog >= 1) { clearInterval(mi); t.setC(p => ({...p, membraneProgress:1})); return; }
          t.setC(p => ({...p, membraneProgress:prog}));
        }, 50);
        t.T.push(mi);
      }, 800);
    }},

  { id:9, title:"Money Moves", sub:"Profits flow Hexagon → F6. F6 funds the organism — F0, F1. F1 steers F6. The financial bloodstream.",
    color:"#7C3AED", dur:9000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6"],
    showPeopleSpine: true,
    arcs:[
      {f:"hexagon",t:"F6",c:"#7C3AED",l:"profits",off:55,ly:25},{f:"F6",t:"hexagon",c:"#7C3AED",l:"funds",off:55,ly:25},
      {f:"F6",t:"F1",c:"#7C3AED",l:"funds",off:40},{f:"F1",t:"F6",c:"#7C3AED",l:"steers",off:40},
      {f:"F6",t:"F0",c:"#7C3AED",l:"funds"},
    ],
    particles:[
      {f:"hexagon",t:"F6",c:"#7C3AED",del:0.3,off:55},{f:"F6",t:"hexagon",c:"#7C3AED",del:1.5,off:55},
      {f:"F6",t:"F1",c:"#7C3AED",del:2.0,off:40},{f:"F1",t:"F6",c:"#7C3AED",del:3.0,off:40},
      {f:"F6",t:"F0",c:"#7C3AED",del:2.5},
    ] },

  { id:10, title:"People Sustain", sub:"People close the loop. F0 feeds the backbone. Culture sustains the organism.",
    color:"#F97316", dur:7000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6"],
    showPeopleSpine: true,
    arcs:[{f:"F0",t:"hexagon",c:"#F97316",l:"sustains"},{f:"F0",t:"F1",c:"#F97316",l:"enables",off:-60}],
    particles:[{f:"F0",t:"hexagon",c:"#F97316",del:0.3},{f:"F0",t:"F1",c:"#F97316",del:1.2,off:-60}] },

  { id:11, title:"Friction Detected", sub:"Week 3 — vessel underperforms. Issues flow from the Hexagon into the DNA Triangle. Execution hits headwinds. Issues flow from Execution to DNA Triangle.",
    color:"#DC2626", dur:8000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8"],
    arcs:[{f:"hexagon",t:"F8",c:"#DC2626",l:"issues"}],
    particles:[{f:"hexagon",t:"F8",c:"#DC2626",del:0.5}],
    showDNA: true, showSpine: true,
    timeline: t => {
      t.s(() => t.setC(p => ({...p, f5Amber:true, spineLit:true})), 0);
      t.s(() => t.setC(p => ({...p, dnaLit:true})), 2500);
      const dnaGap = 600;
      for (let i = 0; i < 8; i++) {
        t.s(() => t.setC(p => ({...p, dnaNodesRevealed: i + 1})), 3000 + i * dnaGap);
      }
    }},

  { id:12, title:"Intelligence Responds", sub:"Issues are enriched into intelligence by way of Signal and Density tests. Balloon density and Operational decisions inform execution. Policies are tweaked or designed. The organism learns and grows with the right priorities.",
    color:"#059669", dur:14000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8"],
    showSpine: true, showDNA: true,
    arcs:[{f:"hexagon",t:"F8",c:"#DC2626",l:"issues"}],
    particles:[{f:"hexagon",t:"F8",c:"#DC2626",del:0.5}],
    timeline: t => {
      t.s(() => t.setC(p => ({...p, spineLit:true, dnaLit:true})), 0);
      t.s(() => t.hexG(true), 2500); t.s(() => t.hexG(false), 3500);
      t.s(() => t.setC(p => ({...p, dnaFeedback:true})), 6000);
    }},

  { id:13, title:"Technology Builds", sub:"Capability demands infrastructure. F9 builds it, enables the Hexagon and feeds Operations (Mission, Objective or Activity depending on magnitude).",
    color:"#2563EB", dur:9000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8","F9"],
    showSpine: true, showDNA: true,
    timeline: t => {
      t.s(() => t.setC(p => ({...p, spineLit:true, dnaLit:true})), 0);
      t.s(() => t.setC(p => ({...p, capToF9:true})), 2000);
      t.s(() => t.setC(p => ({...p, f9ToHex:true})), 4500);
      t.s(() => t.setC(p => ({...p, f9ToOps:true})), 5500);
    }},

  { id:14, title:"The System Breathes", sub:"Every event compounds. Every cycle makes the organism smarter and stronger.",
    color:"#475569", dur:15000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8","F9"],
    showSpine: true, showDNA: true, showPeopleSpine: true,
    arcs:[
      {f:"F0",t:"hexagon",c:"#F97316",l:"sustains"},{f:"F0",t:"F1",c:"#F97316",l:"enables",off:-60},
      {f:"F1",t:"hexagon",c:"#059669",l:"directs",off:-55},{f:"F2",t:"hexagon",c:"#D4A017",l:"content"},
      {f:"F3",t:"F4",c:"#0D9488",l:"OU"},
      {f:"F4",t:"hexagon",c:"#E04848",l:"assembles"},
      {f:"F3",t:"hexagon",c:"#0D9488",l:"OU"},{f:"F5",t:"hexConsumption",c:"#F97316",l:"orders",off:-40},
      {f:"hexagon",t:"F6",c:"#7C3AED",l:"profits",off:55,ly:25},{f:"F6",t:"hexagon",c:"#7C3AED",l:"funds",off:55,ly:25},
      {f:"F6",t:"F1",c:"#7C3AED",l:"funds",off:40},{f:"F1",t:"F6",c:"#7C3AED",l:"steers",off:40},
      {f:"F6",t:"F0",c:"#7C3AED",l:"funds"},
    ],
    particles:[
      {f:"F0",t:"hexagon",c:"#F97316",del:0,freq:2},{f:"F0",t:"F1",c:"#F97316",del:0.5,off:-60,freq:3},
      {f:"F1",t:"hexagon",c:"#059669",del:1.0,off:-55,freq:4},{f:"F2",t:"hexagon",c:"#D4A017",del:0.3,freq:4},
      {f:"F3",t:"F4",c:"#0D9488",del:1.1,freq:3},
      {f:"F4",t:"hexagon",c:"#E04848",del:1.5,freq:3},
      {f:"F3",t:"hexagon",c:"#0D9488",del:0.7,freq:3},{f:"F5",t:"hexConsumption",c:"#F97316",del:0.4,off:-40,freq:1},
      {f:"hexagon",t:"F6",c:"#7C3AED",del:0.2,off:55,freq:1},{f:"F6",t:"hexagon",c:"#7C3AED",del:1.3,off:55,freq:3},
      {f:"F6",t:"F1",c:"#7C3AED",del:1.8,off:40,freq:2},{f:"F1",t:"F6",c:"#7C3AED",del:2.5,off:40,freq:2},
      {f:"F6",t:"F0",c:"#7C3AED",del:2.0,freq:2},
    ],
    timeline: t => {
      t.s(() => t.setC(p => ({...p, spineLit:true, dnaLit:true, capToF9:true, f9ToOps:true, f9ToHex:true, dnaFeedback:true, hexHeartbeat:true})), 0);
      t.s(() => t.hexG(true), 2000); t.s(() => t.hexG(false), 4000);
      t.s(() => t.hexG(true), 5000); t.s(() => t.hexG(false), 7000);
      t.s(() => t.setC(p => ({...p, allIssue:true})), 3000);
      let h = 42;
      t.s(() => {
        const hi = setInterval(() => {
          h += 0.2; if (h >= 43) { clearInterval(hi); t.setC(p => ({...p, healthDisp:43})); return; }
          t.setC(p => ({...p, healthDisp:Math.round(h*10)/10}));
        }, 100);
        t.T.push(hi);
      }, 8000);
    }},

  { id:15, title:"The Subatomic Bonding", sub:"Every click hides a deeper event. Zoom into Content — three dimensions must align before a module clicks: SAP.",
    color:"#8B5CF6", dur:22000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8","F9"],
    showSpine: true, showDNA: true, showPeopleSpine: true,
    timeline: t => {
      t.s(() => t.setC(p => ({...p, spineLit:true, dnaLit:true, capToF9:true, f9ToOps:true, f9ToHex:true, dnaFeedback:true})), 0);
      // ph0: hex zooms in
      t.s(() => t.setC(p => ({...p, sapFocus:true, sapPhase:0})), 300);
      // ph1: Content flashes red 3x (2s pause first)
      t.s(() => t.setC(p => ({...p, sapPhase:1})), 2600);
      // ph2: Content becomes red sphere
      t.s(() => t.setC(p => ({...p, sapPhase:2})), 5500);
      // ph3: Blue offer sphere arrives, orbiting starts
      t.s(() => t.setC(p => ({...p, sapPhase:3})), 7200);
      // ph4-6: SAP cards S, A, P
      t.s(() => t.setC(p => ({...p, sapPhase:4})), 9500);
      t.s(() => t.setC(p => ({...p, sapPhase:5})), 11000);
      t.s(() => t.setC(p => ({...p, sapPhase:6})), 12500);
      // ph7: converging
      t.s(() => t.setC(p => ({...p, sapPhase:7})), 14500);
      // ph8: bond flash
      t.s(() => t.setC(p => ({...p, sapPhase:8})), 16000);
      // No cleanup needed — scene transition to Fractal preserves zoom
    }},

  { id:16, title:"Fractals and Chains", sub:"Zoom into Operations. Inside — the same six modules. Same pattern, different scale. The Hexagon is fractal across all modules.",
    color:"#0D9488", dur:30000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8","F9"],
    showSpine: true, showDNA: true, showPeopleSpine: true,
    timeline: t => {
      t.s(() => t.setC(p => ({...p, spineLit:true, dnaLit:true, capToF9:true, f9ToOps:true, f9ToHex:true, dnaFeedback:true})), 0);
      // ph0: already zoomed in, Ops normal (2s pause)
      t.s(() => t.setC(p => ({...p, fractalPhase:0})), 0);
      // ph1: Operations flashes red 3x
      t.s(() => t.setC(p => ({...p, fractalPhase:1})), 2000);
      // ph2: Operations becomes red need sphere
      t.s(() => t.setC(p => ({...p, fractalPhase:2})), 4800);
      // ph3: Blue offer sphere orbits
      t.s(() => t.setC(p => ({...p, fractalPhase:3})), 6700);
      // ph4: Blue sphere stops, settles to the right, pulses
      t.s(() => t.setC(p => ({...p, fractalPhase:4})), 9200);
      // ph5: Blue sphere expands into floating secondary hexagon
      t.s(() => t.setC(p => ({...p, fractalPhase:5})), 11500);
      // ph6: Consumption in secondary hex flashes red 3x
      t.s(() => t.setC(p => ({...p, fractalPhase:6})), 14500);
      // ph7: Secondary hex slides — Consumption locks under Operations
      t.s(() => t.setC(p => ({...p, fractalPhase:7})), 17300);
      // ph8: CLICK
      t.s(() => t.setC(p => ({...p, fractalPhase:8})), 19500);
      // ph9: Label Ops=Consumption
      t.s(() => t.setC(p => ({...p, fractalPhase:9})), 22000);
      // ph10: Cascading ghost hexagons
      t.s(() => t.setC(p => ({...p, fractalPhase:10})), 24500);
      // ph11: Final label
      t.s(() => t.setC(p => ({...p, fractalPhase:11})), 27500);
      // Stay on magnified view — no cleanup
    }},

  { id:17, title:"The System Breathes", sub:"Zoom out. The organism breathes. Every cycle compounds and makes the organism smarter and stronger",
    color:"#475569", dur:12000,
    show:["F0","F1","hexagon","F2","F3","F4","F5","F6","F8","F9"],
    showSpine: true, showDNA: true, showPeopleSpine: true,
    arcs:[
      {f:"F0",t:"hexagon",c:"#F97316",l:"sustains"},{f:"F0",t:"F1",c:"#F97316",l:"enables",off:-60},
      {f:"F1",t:"hexagon",c:"#059669",l:"directs",off:-55},{f:"F2",t:"hexagon",c:"#D4A017",l:"content"},
      {f:"F3",t:"F4",c:"#0D9488",l:"OU"},
      {f:"F4",t:"hexagon",c:"#E04848",l:"assembles"},
      {f:"F3",t:"hexagon",c:"#0D9488",l:"OU"},{f:"F5",t:"hexConsumption",c:"#F97316",l:"orders",off:-40},
      {f:"hexagon",t:"F6",c:"#7C3AED",l:"profits",off:55,ly:25},{f:"F6",t:"hexagon",c:"#7C3AED",l:"funds",off:55,ly:25},
      {f:"F6",t:"F1",c:"#7C3AED",l:"funds",off:40},{f:"F1",t:"F6",c:"#7C3AED",l:"steers",off:40},
      {f:"F6",t:"F0",c:"#7C3AED",l:"funds"},
    ],
    particles:[
      {f:"F0",t:"hexagon",c:"#F97316",del:0,freq:2},{f:"F0",t:"F1",c:"#F97316",del:0.5,off:-60,freq:3},
      {f:"F1",t:"hexagon",c:"#059669",del:1.0,off:-55,freq:4},{f:"F2",t:"hexagon",c:"#D4A017",del:0.3,freq:4},
      {f:"F3",t:"F4",c:"#0D9488",del:1.1,freq:3},
      {f:"F4",t:"hexagon",c:"#E04848",del:1.5,freq:3},
      {f:"F3",t:"hexagon",c:"#0D9488",del:0.7,freq:3},{f:"F5",t:"hexConsumption",c:"#F97316",del:0.4,off:-40,freq:1},
      {f:"hexagon",t:"F6",c:"#7C3AED",del:0.2,off:55,freq:1},{f:"F6",t:"hexagon",c:"#7C3AED",del:1.3,off:55,freq:3},
      {f:"F6",t:"F1",c:"#7C3AED",del:1.8,off:40,freq:2},{f:"F1",t:"F6",c:"#7C3AED",del:2.5,off:40,freq:2},
      {f:"F6",t:"F0",c:"#7C3AED",del:2.0,freq:2},
    ],
    timeline: t => {
      t.s(() => t.setC(p => ({...p, spineLit:true, dnaLit:true, capToF9:true, f9ToOps:true, f9ToHex:true, dnaFeedback:true, chainVisible:false, dimAll:false, hexHeartbeat:true})), 0);
      t.s(() => t.hexG(true), 2000); t.s(() => t.hexG(false), 4000);
      t.s(() => t.hexG(true), 5000); t.s(() => t.hexG(false), 7000);
      t.s(() => t.setC(p => ({...p, allIssue:true})), 3000);
      let h = 43;
      t.s(() => {
        const hi = setInterval(() => {
          h += 0.2; if (h >= 44) { clearInterval(hi); t.setC(p => ({...p, healthDisp:44})); return; }
          t.setC(p => ({...p, healthDisp:Math.round(h*10)/10}));
        }, 100);
        t.T.push(hi);
      }, 8000);
    }},
];

// =====================================================
// HELPERS
// =====================================================
const FREQ_DUR = { 1: 1, 2: 2, 3: 4, 4: 8 };
function makePath(x1,y1,x2,y2) {
  const dx=Math.abs(x2-x1),dy=Math.abs(y2-y1);
  if (dx>dy*1.2) { const m=(x1+x2)/2; return `M${x1},${y1} C${m},${y1} ${m},${y2} ${x2},${y2}`; }
  if (dy>dx*1.2) { const m=(y1+y2)/2; return `M${x1},${y1} C${x1},${m} ${x2},${m} ${x2},${y2}`; }
  const m=(x1+x2)/2; return `M${x1},${y1} C${m},${y1} ${m},${y2} ${x2},${y2}`;
}
function makePathOff(x1,y1,x2,y2,off) {
  const mx=(x1+x2)/2, my=(y1+y2)/2;
  const dx=x2-x1, dy=y2-y1;
  const len=Math.sqrt(dx*dx+dy*dy)||1;
  const px=-dy/len, py=dx/len;
  const cx=mx+px*off, cy=my+py*off;
  return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
}
function bezMidOff(x1,y1,x2,y2,off) {
  const mx=(x1+x2)/2, my=(y1+y2)/2;
  const dx=x2-x1, dy=y2-y1;
  const len=Math.sqrt(dx*dx+dy*dy)||1;
  const px=-dy/len, py=dx/len;
  return [0.25*x1 + 0.5*(mx+px*off) + 0.25*x2, 0.25*y1 + 0.5*(my+py*off) + 0.25*y2];
}
function bezMid(x1,y1,x2,y2) {
  const dx=Math.abs(x2-x1),dy=Math.abs(y2-y1);
  let c1x,c1y,c2x,c2y;
  if (dx>dy*1.2) { const m=(x1+x2)/2; c1x=m;c1y=y1;c2x=m;c2y=y2; }
  else if (dy>dx*1.2) { const m=(y1+y2)/2; c1x=x1;c1y=m;c2x=x2;c2y=m; }
  else { c1x=(x1+x2)/2;c1y=y1;c2x=(x1+x2)/2;c2y=y2; }
  return [.125*x1+.375*c1x+.375*c2x+.125*x2,.125*y1+.375*c1y+.375*c2y+.125*y2];
}

// =====================================================
// MAIN
// =====================================================
export default function App() {
  const [selected, setSelected] = useState(null);
  const [selStep, setSelStep] = useState(null);
  const [scene, setScene] = useState(-1);
  const [C, setC] = useState({
    hexGlow:false, hexBorder:0, hexBorderGlow:false, powerup:0,
    mods:{}, dnaLit:false, spineLit:false,
    f5Amber:false, healthDisp:42, allIssue:false, dnaFeedback:false,
    spineRevealed:0, spineParticle:-1,
    directionGlow:false, directionArc:false,
    hexVisible:false, hexModsRevealed:0,
    capToF9:false, f9ToOps:false, f9ToHex:false,
    dnaNodesRevealed:0,
    membraneVisible:false, membraneProgress:0,
    peopleSpineRevealed:0, peopleSpineLit:false,
    lensPhase:0, lensVisible:false,
    sapFocus:false, sapPhase:0,
    fractalZoom:false, fractalPhase:0,
    chainPhase:0, chainVisible:false, dimAll:false,
    hexHeartbeat:false,
    sceneReady:false,
  });

  const CX = 480, CY = 340, HEX_R = 72;

  const flowPos = useMemo(() => {
    const m = { hexagon: { x: CX, y: CY } };
    const consA = (270 * Math.PI) / 180;
    m.hexConsumption = { x: CX + 54 * Math.cos(consA), y: CY + 54 * Math.sin(consA) };
    Object.entries(FPOS).forEach(([id, p]) => { m[id] = p; });
    return m;
  }, []);

  const health = useMemo(() => {
    let t=0,s=0; const w={active:1,partial:.5,external:.4,gap:.1,critical:0};
    FLOWS.forEach(f => f.steps.forEach(st => { t++; s += w[st.status]||0; }));
    return Math.round(s/t*100);
  }, []);

  const sc = scene >= 0 ? SCENES[scene] : null;
  const playing = scene >= 0;

  const revealed = useMemo(() => {
    if (!playing) return new Set(ALL_IDS);
    const s = new Set();
    for (let i = 0; i <= scene; i++) (SCENES[i].show||[]).forEach(id => s.add(id));
    return s;
  }, [scene, playing]);

  const spineCount = !playing ? SPINE.length : (C.spineLit ? SPINE.length : C.spineRevealed);
  const pSpineCount = !playing ? PEOPLE_SPINE.length : (C.peopleSpineLit ? PEOPLE_SPINE.length : C.peopleSpineRevealed);
  const hexFullyFormed = !playing || C.hexModsRevealed >= 6;
  const showSpine = selected === "F1" || selected === "F8" || (playing && (sc?.showSpine || C.spineLit || C.spineRevealed > 0));
  const showDNA = selected === "F8" || (playing && (sc?.showDNA || C.dnaLit));
  const showPeopleSpine = selected === "F0" || (playing && (sc?.showPeopleSpine || C.peopleSpineLit || C.peopleSpineRevealed > 0));

  const spinePos = useMemo(() => {
    const f1 = flowPos.F1;
    const sx = f1.x + 10, sy = f1.y + 60;
    const m = {};
    SPINE.forEach((s, i) => { m[s.id] = { x: sx + (s.indent ? 22 : 0), y: sy + i * 36 }; });
    return m;
  }, [flowPos]);

  const pSpinePos = useMemo(() => {
    const f0 = flowPos.F0;
    const sx = f0.x + 10, sy = f0.y + 60;
    const m = {};
    PEOPLE_SPINE.forEach((s, i) => { m[s.id] = { x: sx + (s.indent ? 22 : 0), y: sy + i * 32 }; });
    return m;
  }, [flowPos]);

  useEffect(() => {
    if (scene < 0) return;
    const T = [];
    const s = (fn, ms) => T.push(setTimeout(fn, ms));
    const hexG = (v) => setC(p => ({...p, hexGlow:v}));
    const flash3 = (name, color, startMs) => {
      for (let i = 0; i < 3; i++) {
        s(() => setC(p => ({...p, mods:{...p.mods,[name]:{color,flash:true}}})), startMs + i*400);
        s(() => setC(p => ({...p, mods:{...p.mods,[name]:{color,flash:false}}})), startMs + i*400 + 200);
      }
      s(() => setC(p => ({...p, mods:{...p.mods,[name]:{color,flash:false}}})), startMs + 1200);
    };
    setC(p => ({...p,
      hexGlow:false, hexBorder:0, hexBorderGlow:false, powerup:0,
      f5Amber:false, allIssue:false, dnaFeedback:false, spineParticle:-1,
      directionGlow:false, directionArc:false, capToF9:false, f9ToOps:false, f9ToHex:false,
      lensVisible:false, lensPhase:0, sapFocus:false, sapPhase:0,
      fractalZoom:false, fractalPhase:0,
      chainVisible:false, chainPhase:0, dimAll:false, hexHeartbeat:false,
      sceneReady:false,
      ...(scene === 0 ? { mods:{}, dnaLit:false, spineLit:false, healthDisp:42, spineRevealed:0, hexVisible:false, hexModsRevealed:0, dnaNodesRevealed:0, membraneVisible:false, membraneProgress:0, peopleSpineRevealed:0, peopleSpineLit:false } : {}),
      ...(scene >= 1 ? { peopleSpineLit:true, peopleSpineRevealed: PEOPLE_SPINE.length } : {}),
      ...(scene >= 2 ? { spineRevealed: SPINE.length } : {}),
      ...(scene >= 3 ? { hexVisible:true, hexModsRevealed:6 } : {}),
      ...(scene >= 9 ? { membraneVisible:true, membraneProgress:1 } : {}),
      ...(scene >= 12 ? { dnaNodesRevealed:8 } : {}),
      // Keep zoom when transitioning from SAP Bond → Fractal
      ...(scene === 16 ? { fractalZoom:true } : {}),
    }));
    if (sc?.timeline) sc.timeline({ s, hexG, flash3, setC, T });
    // Set sceneReady after scene duration (no auto-advance)
    s(() => setC(p => ({...p, sceneReady:true})), sc?.dur || 7000);
    return () => T.forEach(id => { clearTimeout(id); clearInterval(id); });
  }, [scene]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight" && playing && scene < SCENES.length-1) setScene(p => p+1);
      if (e.key === "ArrowLeft" && playing && scene > 0) setScene(p => p-1);
      if (e.key === "Escape") { setScene(-1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [playing, scene]);

  const isVis = useCallback((id) => {
    if (!playing) return true;
    if (id === "hexagon" || id === "hexConsumption") return C.hexVisible || revealed.has("hexagon");
    return revealed.has(id);
  }, [playing, revealed, C.hexVisible]);

  const clickFlow = (id) => {
    if (playing) return;
    setSelected(p => p === id ? null : id);
    setSelStep(null);
  };
  const selectedFlow = FLOWS.find(f => f.id === selected);

  return (
    <div style={{ width:"100%", height:"100vh", background:"#F7F8FC", position:"relative", overflow:"hidden", fontFamily:"system-ui,-apple-system,sans-serif" }}>

      <div style={{ position:"absolute", top:0, left:0, right:0, padding: playing ? "8px 10px" : "10px 18px", display:"flex", alignItems:"center", gap:16, zIndex:30, background: playing ? "none" : "linear-gradient(180deg,rgba(247,248,252,0.98) 70%,rgba(247,248,252,0))" }}>
        {playing && sc ? (
          <div style={{ flex:1, minWidth:0, display:"flex", alignItems:"center" }}>
            <div style={{ flex:1, background:"rgba(15,23,42,0.88)", backdropFilter:"blur(20px)", borderRadius:12, padding:"8px 16px", boxShadow:"0 4px 24px rgba(0,0,0,0.15)", display:"flex", alignItems:"center", gap:10 }}>
              <button onClick={() => scene>0&&setScene(p=>p-1)} style={{ width:24, height:24, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.12)", background:"transparent", color:"#94A3B8", cursor:scene===0?"default":"pointer", opacity:scene===0?0.3:1, fontSize:11, flexShrink:0 }}>←</button>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
                  <span style={{ fontSize:9, fontWeight:700, color:sc.color, textTransform:"uppercase", letterSpacing:"0.1em" }}>{scene+1}/{SCENES.length}</span>
                  <span style={{ fontSize:13, fontWeight:700, color:"#F8FAFC", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{sc.title}</span>
                </div>
                <div style={{ fontSize:11, color:"#94A3B8", lineHeight:1.4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sc.sub}</div>
              </div>
              <button onClick={() => scene<SCENES.length-1&&setScene(p=>p+1)} style={{ width:24, height:24, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.12)", background:"transparent", color:"#94A3B8", cursor:scene>=SCENES.length-1?"default":"pointer", opacity:scene>=SCENES.length-1?0.3:1, fontSize:11, flexShrink:0 }}>→</button>
              <button onClick={() => {setScene(-1);}} style={{ padding:"3px 10px", borderRadius:6, border:"1px solid rgba(255,255,255,0.08)", background:"transparent", fontSize:9, fontWeight:600, color:"#64748B", cursor:"pointer", flexShrink:0 }}>ESC</button>
            </div>
          </div>
        ) : (
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:10, color:"#64748B" }}>Health</span>
            <div style={{ width:72, height:5, borderRadius:3, background:"#E2E8F0", overflow:"hidden" }}>
              <div style={{ width:`${health}%`, height:"100%", borderRadius:3, background: health > 60 ? "#22C55E" : health > 30 ? "#F59E0B" : "#EF4444" }} />
            </div>
            <span style={{ fontSize:10, fontWeight:700, color:"#1E293B" }}>{health}%</span>
          </div>
        )}
      </div>

      {playing && sc && (
        <div style={{ position:"absolute", top:48, left:0, right:0, height:3, borderRadius:0, background:"rgba(0,0,0,0.06)", display:"flex", gap:1, overflow:"hidden", zIndex:30 }}>
          {SCENES.map((s, i) => (
            <div data-testid="arch-livingsystem-clickable-div" key={i} onClick={() => setScene(i)} style={{
              flex:1, cursor:"pointer", borderRadius:2, overflow:"hidden",
              background: i < scene ? s.color : i === scene ? `${s.color}40` : "rgba(0,0,0,0.03)" }}>
              {i === scene && <div style={{ height:"100%", background:s.color, borderRadius:2, width: C.sceneReady ? "100%" : "60%", transition:"width 0.5s ease" }} />}
              {i < scene && <div style={{ height:"100%", background:s.color, borderRadius:2 }} />}
            </div>
          ))}
        </div>
      )}

      <svg viewBox="-20 -50 1040 1010" style={{ width:"100%", height:"100%", position:"absolute", top:0, left:0 }}>
        <defs>
          <filter id="sh"><feDropShadow dx="0" dy="1" stdDeviation="2" floodOpacity="0.06" /></filter>
          <filter id="shB"><feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.08" /></filter>
          <filter id="glowGold"><feDropShadow dx="0" dy="0" stdDeviation="6" floodColor="#D4A017" floodOpacity="0.7" /></filter>
          <filter id="molGlow"><feGaussianBlur in="SourceGraphic" stdDeviation="8" /><feColorMatrix type="matrix" values="0 0 0 0 0.973  0 0 0 0 0.573  0 0 0 0 0.094  0 0 0 0.15 0"/></filter>
          <linearGradient id="molFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFF7ED" stopOpacity="0.25" />
            <stop offset="50%" stopColor="#FFFBEB" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#FFF1E6" stopOpacity="0.22" />
          </linearGradient>
          <linearGradient id="molStroke" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FB923C" />
            <stop offset="50%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#EA580C" />
          </linearGradient>
          <marker id="arr" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#94A3B8" /></marker>
          <marker id="arrC" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#DC2626" /></marker>
          <marker id="arrG" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#059669" /></marker>
          <marker id="arrGold" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#D4A017" /></marker>
          <marker id="arrO" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#F97316" /></marker>
          <marker id="arrP" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto"><polygon points="0 0,7 2.5,0 5" fill="#7C3AED" /></marker>
          <radialGradient id="needGrad" cx="40%" cy="40%"><stop offset="0%" stopColor="#FCA5A5"/><stop offset="100%" stopColor="#EF4444"/></radialGradient>
          <radialGradient id="offerGrad" cx="40%" cy="40%"><stop offset="0%" stopColor="#93C5FD"/><stop offset="100%" stopColor="#3B82F6"/></radialGradient>
        </defs>

        {showSpine && isVis("F1") && (
          <g>
            {spineCount > 0 && <text x={spinePos.sp_purpose.x} y={spinePos.sp_purpose.y - 16} fill="#059669" fontSize="8" fontWeight="700" fontFamily="system-ui" opacity={0.4} letterSpacing="0.08em" style={{ animation:"fadeIn 0.4s ease" }}>PURPOSE → EXECUTION</text>}
            {SPINE_PAIRS.map(([a,b], i) => {
              if (i + 1 >= spineCount) return null;
              const pa = spinePos[a], pb = spinePos[b];
              return <line key={i} x1={pa.x} y1={pa.y + 12} x2={pb.x} y2={pb.y - 12} stroke="#D1D5DB" strokeWidth={1} style={{ animation:"fadeIn 0.3s ease" }} />;
            })}
            {SPINE.map((n, idx) => {
              if (idx >= spineCount) return null;
              const p = spinePos[n.id];
              const glowing = n.id === "sp_direction" && C.directionGlow;
              return (
                <g key={n.id} style={{ animation:"fadeIn 0.4s ease" }}>
                  {glowing && <rect x={p.x - 56} y={p.y - 16} width={112} height={32} rx={10} fill="none" stroke="#D4A017" strokeWidth={3} opacity={0.6} filter="url(#glowGold)" style={{ animation:"issuePulse 1.5s ease-in-out infinite" }} />}
                  <rect x={p.x - (n.main ? 52 : 44)} y={p.y - 12} width={n.main ? 104 : 88} height={24} rx={6}
                    fill={glowing ? "#FFFBEB" : n.main ? "#ECFDF5" : "#F8FAFC"}
                    stroke={glowing ? "#D4A017" : n.main ? "#059669" : "#D1D5DB"}
                    strokeWidth={glowing ? 2.5 : n.main ? 1.2 : 0.8} filter="url(#sh)" />
                  <text x={p.x} y={p.y - 1} fill={glowing ? "#92400E" : n.main ? "#047857" : "#64748B"} fontSize={n.main ? "10" : "9"} fontWeight={n.main ? "700" : "600"} textAnchor="middle" fontFamily="system-ui">{n.label}</text>
                  <text x={p.x} y={p.y + 9} fill={glowing ? "#D4A017" : n.main ? "#059669" : "#94A3B8"} fontSize="7" textAnchor="middle" fontFamily="system-ui">{n.sub}</text>
                </g>
              );
            })}
            {C.spineParticle >= 0 && C.spineParticle < SPINE.length - 1 && (() => {
              const fromP = spinePos[SPINE[C.spineParticle].id];
              const toP = spinePos[SPINE[C.spineParticle + 1].id];
              const path = `M${fromP.x},${fromP.y + 12} L${toP.x},${toP.y - 12}`;
              return (
                <g key={`sp-${C.spineParticle}`}>
                  <circle r={5} fill="#059669" opacity={0.9}>
                    <animateMotion dur="0.8s" path={path} fill="freeze" />
                    <animate attributeName="opacity" values="0.9;0.9;0" keyTimes="0;0.7;1" dur="0.8s" fill="freeze" />
                  </circle>
                  <circle r={3} fill="#059669" opacity={0.5}>
                    <animateMotion dur="0.8s" begin="0.1s" path={path} fill="freeze" />
                    <animate attributeName="opacity" values="0;0.5;0" keyTimes="0;0.5;1" dur="0.8s" begin="0.1s" fill="freeze" />
                  </circle>
                </g>
              );
            })()}
            {C.directionArc && (() => {
              const dir = spinePos.sp_direction;
              const arcPath = makePath(dir.x - 52, dir.y, CX, CY);
              return (
                <g style={{ animation:"fadeIn 0.5s ease" }}>
                  <path d={arcPath} fill="none" stroke="#D4A017" strokeWidth={2.5} opacity={0.6} markerEnd="url(#arrGold)" />
                  {[0, 0.3].map((off, di) => (
                    <circle key={`da${di}`} r={6 - di * 2} fill="#D4A017" opacity={0.8 - di * 0.3}>
                      <animateMotion dur="2s" repeatCount="indefinite" begin={`${off}s`} path={arcPath} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                      <animate attributeName="opacity" values={`0;${0.8-di*0.3};${0.8-di*0.3};0`} keyTimes="0;0.1;0.8;1" dur="2s" repeatCount="indefinite" begin={`${off}s`} />
                    </circle>
                  ))}
                </g>
              );
            })()}
          </g>
        )}

        {showPeopleSpine && isVis("F0") && (
          <g>
            {pSpineCount > 0 && <text x={pSpinePos.ps_codex.x} y={pSpinePos.ps_codex.y - 16} fill="#F97316" fontSize="8" fontWeight="700" fontFamily="system-ui" opacity={0.4} letterSpacing="0.08em" style={{ animation:"fadeIn 0.4s ease" }}>CODEX → PROFILES</text>}
            {PEOPLE_PAIRS.map(([a,b], i) => {
              if (i + 1 >= pSpineCount) return null;
              const pa = pSpinePos[a], pb = pSpinePos[b];
              return <line key={`pp${i}`} x1={pa.x} y1={pa.y + 10} x2={pb.x} y2={pb.y - 10} stroke="#FDBA74" strokeWidth={1} style={{ animation:"fadeIn 0.3s ease" }} />;
            })}
            {PEOPLE_SPINE.map((n, idx) => {
              if (idx >= pSpineCount) return null;
              const p = pSpinePos[n.id];
              return (
                <g key={n.id} style={{ animation:"fadeIn 0.4s ease" }}>
                  <rect x={p.x - (n.main ? 52 : 44)} y={p.y - 10} width={n.main ? 104 : 88} height={20} rx={5}
                    fill={n.main ? "#FFF7ED" : "#FFFBEB"}
                    stroke={n.main ? "#F97316" : "#FDBA74"}
                    strokeWidth={n.main ? 1.2 : 0.8} filter="url(#sh)" />
                  <text x={p.x} y={p.y + 1} fill={n.main ? "#EA580C" : "#92400E"} fontSize={n.main ? "9" : "8"} fontWeight={n.main ? "700" : "600"} textAnchor="middle" fontFamily="system-ui">{n.label}</text>
                  <text x={p.x} y={p.y + 9} fill={n.main ? "#F97316" : "#D97706"} fontSize="6" textAnchor="middle" fontFamily="system-ui" opacity={0.7}>{n.sub}</text>
                </g>
              );
            })}
          </g>
        )}

        {(C.membraneVisible || !playing) && (() => {
          const mx = 145, my = 195, mw = 620, mh = 385, mr = 30;
          const perim = 2 * (mw + mh - 4 * mr) + 2 * Math.PI * mr;
          const prog = !playing ? 1 : C.membraneProgress;
          const dashOff = perim * (1 - prog);
          return (
            <g>
              <rect x={mx-6} y={my-6} width={mw+12} height={mh+12} rx={mr+6}
                fill="none" stroke="#F97316" strokeWidth={8} opacity={prog * 0.06}
                style={{ filter:"url(#molGlow)" }} />
              <rect x={mx} y={my} width={mw} height={mh} rx={mr}
                fill="url(#molFill)" opacity={prog}
                stroke="none" />
              <rect x={mx+3} y={my+3} width={mw-6} height={mh-6} rx={mr-2}
                fill="none" stroke="#FDBA74" strokeWidth={0.5}
                opacity={prog * 0.25} />
              <rect x={mx} y={my} width={mw} height={mh} rx={mr}
                fill="none" stroke="url(#molStroke)" strokeWidth={2}
                strokeDasharray="10 5"
                opacity={Math.min(prog * 1.5, 0.5)} />
              {prog < 1 && (
                <rect x={mx} y={my} width={mw} height={mh} rx={mr}
                  fill="none" stroke="#F97316" strokeWidth={2.5}
                  strokeDasharray={perim}
                  strokeDashoffset={dashOff}
                  opacity={0.75}
                  strokeLinecap="round" />
              )}
              {prog > 0.8 && (
                <g opacity={(prog - 0.8) * 5 * 0.3}>
                  <circle cx={mx+mr} cy={my+mr} r={3} fill="#F97316" opacity={0.4} />
                  <circle cx={mx+mw-mr} cy={my+mr} r={3} fill="#F97316" opacity={0.4} />
                  <circle cx={mx+mr} cy={my+mh-mr} r={3} fill="#F97316" opacity={0.4} />
                  <circle cx={mx+mw-mr} cy={my+mh-mr} r={3} fill="#F97316" opacity={0.4} />
                </g>
              )}
              <g style={{ opacity: prog > 0.4 ? Math.min((prog - 0.4) * 3, 0.7) : 0, transition:"opacity 0.3s" }}>
                <rect x={mx + mw/2 - 68} y={my - 13} width={136} height={20} rx={10}
                  fill="#FFF7ED" stroke="#FDBA74" strokeWidth={0.8} />
                <text x={mx + mw/2} y={my + 1} fill="#EA580C" fontSize="9.5" fontWeight="800"
                  textAnchor="middle" fontFamily="system-ui" letterSpacing="0.14em">BUSINESS CELL</text>
              </g>
              <g style={{ opacity: prog > 0.6 ? Math.min((prog - 0.6) * 4, 0.4) : 0, transition:"opacity 0.3s" }}>
                <text x={mx + mw/2} y={my + 16} fill="#9A3412" fontSize="7" fontWeight="500"
                  textAnchor="middle" fontFamily="system-ui">Hexagon · Content · Operator · Offering · Reaction</text>
              </g>
              {((!playing) || (playing && scene >= 9)) && (
                <g style={{ opacity: 0.4, animation:"fadeIn 0.6s ease" }}>
                  <rect x={750} y={24} width={120} height={46} rx={8}
                    fill="#F8FAFC" stroke="#E2E8F0" strokeWidth={0.6} />
                  <text x={810} y={40} fill="#475569" fontSize="8" fontWeight="800"
                    textAnchor="middle" fontFamily="system-ui" letterSpacing="0.1em">ORGANIZATIONAL</text>
                  <text x={810} y={51} fill="#475569" fontSize="8" fontWeight="800"
                    textAnchor="middle" fontFamily="system-ui" letterSpacing="0.1em">SYSTEM</text>
                  <text x={810} y={63} fill="#64748B" fontSize="6.5" fontWeight="500"
                    textAnchor="middle" fontFamily="system-ui">universal layer</text>
                </g>
              )}
            </g>
          );
        })()}

        {(() => {
          const fp = {x:CX,y:CY}, tp = flowPos.F8;
          if (!fp || !tp) return null;
          const active = (selected === "F8" && !playing) || (playing && C.allIssue);
          const path = makePath(fp.x, fp.y, tp.x, tp.y);
          const [mx, my] = bezMid(fp.x, fp.y, tp.x, tp.y);
          return (
            <g style={{ opacity: active ? 0.6 : 0, transition: "opacity 0.5s" }}>
              <path d={path} fill="none" stroke="#DC2626" strokeWidth={2} markerEnd="url(#arrC)" />
              {active && <g><rect x={mx-18} y={my-8} width={36} height={14} rx={7} fill="#FEF2F2" stroke="#FECACA" strokeWidth={0.5} opacity={0.95}/><text x={mx} y={my+2} fill="#DC2626" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">issues</text></g>}
              {active && [0, 0.4].map((off, di) => (
                <circle key={`hfp${di}`} r={4 - di * 1.5} fill="#DC2626" opacity={0.6 - di * 0.2}>
                  <animateMotion dur="2s" repeatCount="indefinite" begin={`${off}s`} path={path} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                  <animate attributeName="opacity" values={`0;${0.6-di*0.2};${0.6-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="2s" repeatCount="indefinite" begin={`${off}s`} />
                </circle>
              ))}
            </g>
          );
        })()}
        {showSpine && spineCount >= SPINE.length && isVis("F8") && (() => {
          const exec = spinePos.sp_execution, tp = flowPos.F8;
          if (!exec || !tp) return null;
          const active = (selected === "F8" && !playing) || (selected === "F1" && !playing) || (playing && (C.spineLit || C.allIssue));
          const path = makePathOff(exec.x, exec.y, tp.x, tp.y, -300);
          const [mx, my] = bezMidOff(exec.x, exec.y, tp.x, tp.y, -300);
          return (
            <g style={{ opacity: active ? 0.7 : 0, transition: "opacity 0.5s" }}>
              <path d={path} fill="none" stroke="#DC2626" strokeWidth={1.8} strokeDasharray="5 3" markerEnd="url(#arrC)" />
              {active && (
                <g>
                  <rect x={mx-18} y={my-8} width={36} height={14} rx={7} fill="#FEF2F2" stroke="#FECACA" strokeWidth={0.5} opacity={0.95}/>
                  <text x={mx} y={my+2} fill="#DC2626" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">issues</text>
                  {[0, 0.4].map((off, di) => (
                    <circle key={`ep${di}`} r={4 - di * 1.5} fill="#DC2626" opacity={0.6 - di * 0.2}>
                      <animateMotion dur="2s" repeatCount="indefinite" begin={`${off}s`} path={path} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                      <animate attributeName="opacity" values={`0;${0.6-di*0.2};${0.6-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="2s" repeatCount="indefinite" begin={`${off}s`} />
                    </circle>
                  ))}
                </g>
              )}
            </g>
          );
        })()}

        {(() => {
          let conns;
          if (playing) {
            conns = (sc?.arcs || []).filter(c => flowPos[c.f] && flowPos[c.t]);
          } else if (selected && flowPos[selected]) {
            const flow = FLOWS.find(f => f.id === selected);
            const col = flow?.color || "#94A3B8";
            const lbl = FLOW_LABELS[selected] || "";
            conns = [];
            if (selected === "F0") {
              conns.push({ f:"F0", t:"hexagon", c:col, l:"sustains" });
              conns.push({ f:"F0", t:"F1", c:col, l:"enables" });
            } else if (selected === "F5") {
              conns.push({ f:"F5", t:"hexConsumption", c:col, l:"orders", off:-40 });
            } else if (selected === "F6") {
              conns.push({ f:"hexagon", t:"F6", c:col, l:"profits", off:55, ly:25 });
              conns.push({ f:"F6", t:"hexagon", c:col, l:"funds", off:55, ly:25 });
            } else if (selected === "F1") {
              conns.push({ f:"F1", t:"hexagon", c:col, l:"directs", off:-55 });
            } else {
              conns.push({ f: selected, t: "hexagon", c: col, l: lbl });
            }
          } else {
            conns = [];
          }
          return conns.map((c, i) => {
            const fp = flowPos[c.f], tp = flowPos[c.t];
            if (!fp || !tp || !isVis(c.f) || !isVis(c.t)) return null;
            const path = c.off ? makePathOff(fp.x, fp.y, tp.x, tp.y, c.off) : makePath(fp.x, fp.y, tp.x, tp.y);
            const [mx, my0] = c.off ? bezMidOff(fp.x, fp.y, tp.x, tp.y, c.off) : bezMid(fp.x, fp.y, tp.x, tp.y);
            const my = my0 + (c.ly || 0);
            return (
              <g key={`cn-${i}`} style={{ animation: "fadeIn 0.4s ease" }}>
                <path d={path} fill="none" stroke={c.c} strokeWidth={2.5} opacity={0.5} strokeDasharray={playing ? "none" : "1400"} style={playing ? undefined : { animation: "drawLine 1s ease forwards" }} />
                {c.l && <g><rect x={mx-22} y={my-9} width={44} height={15} rx={7.5} fill="#fff" stroke={c.c} strokeWidth={0.6} opacity={0.95} filter="url(#sh)"/><text x={mx} y={my+2} fill={c.c} fontSize="8" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{c.l}</text></g>}
              </g>
            );
          });
        })()}

        {playing && (sc?.particles || []).map((p, i) => {
          const fp = flowPos[p.f], tp = flowPos[p.t];
          if (!fp || !tp || !isVis(p.f) || !isVis(p.t)) return null;
          const pathD = p.off ? makePathOff(fp.x, fp.y, tp.x, tp.y, p.off) : makePath(fp.x, fp.y, tp.x, tp.y);
          const dur = p.freq ? FREQ_DUR[p.freq] : 2;
          const durS = `${dur}s`;
          const trail = dur * 0.06;
          return (
            <g key={`p-${scene}-${i}`}>
              {[0, trail, trail*2].map((off, di) => (
                <circle key={di} r={5 - di * 1.5} fill={p.c} opacity={0.8 - di * 0.25}>
                  <animateMotion dur={durS} repeatCount="indefinite" begin={`${(p.del||0) + off}s`} path={pathD} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                  <animate attributeName="opacity" values={`0;${0.8 - di * 0.25};${0.8 - di * 0.25};0`} keyTimes="0;0.1;0.8;1" dur={durS} repeatCount="indefinite" begin={`${(p.del||0) + off}s`} />
                </circle>
              ))}
            </g>
          );
        })}

        {isVis("hexagon") && (
          <g onClick={() => !playing && setSelected(null)} style={{ cursor: playing ? "default" : "pointer", animation: C.hexHeartbeat ? "heartbeat 4s ease-in-out infinite" : (playing && scene === 2 ? "fadeIn 0.8s ease" : "none"), transformOrigin:`${CX}px ${CY}px` }}>
            {C.hexGlow && (
              <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${CX+(HEX_R+12)*Math.cos(a)},${CY+(HEX_R+12)*Math.sin(a)}`;}).join(" ")}
                fill="none" stroke="#D4A017" strokeWidth={6} opacity={0.35} style={{animation:"issuePulse 1s ease-in-out infinite"}} filter="url(#shB)" />
            )}
            <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${CX+HEX_R*Math.cos(a)},${CY+HEX_R*Math.sin(a)}`;}).join(" ")}
              fill={C.powerup>=1&&C.powerup<=4?"#DBEAFE":"#FFFDF5"} stroke={C.powerup>=1&&C.powerup<=4?"#3B82F6":"#D4A017"}
              strokeWidth={C.powerup>=2?3.5:2.2} filter="url(#shB)" style={C.powerup>=1&&C.powerup<=4?{transition:"all 0.3s"}:undefined} />

            {C.powerup >= 1 && C.powerup <= 5 && (
              <g>
                {C.powerup>=1&&C.powerup<=4&&<polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${CX+(HEX_R-4)*Math.cos(a)},${CY+(HEX_R-4)*Math.sin(a)}`;}).join(" ")} fill="#3B82F6" opacity={C.powerup===1?0.15:C.powerup===2?0.25:C.powerup===3?0.35:0.2} style={{transition:"opacity 0.3s"}}/>}
                {C.powerup>=2&&[1,2,3].map(i => <circle key={`sw${i}`} cx={CX} cy={CY} r={HEX_R} fill="none" stroke="#3B82F6" strokeWidth={3-i*0.8} style={{transformOrigin:`${CX}px ${CY}px`,opacity:0,animation:`shockwave ${1.2+i*0.3}s ease-out ${i*0.2}s ${C.powerup<=3?"infinite":"1"} forwards`}}/>)}
                {C.powerup>=2&&C.powerup<=4&&Array.from({length:12},(_,i)=>{const a=(Math.PI*2/12)*i,r1=HEX_R+5,r2=HEX_R+25+(i%3)*12; return <line key={`eb${i}`} x1={CX+r1*Math.cos(a)} y1={CY+r1*Math.sin(a)} x2={CX+r2*Math.cos(a)} y2={CY+r2*Math.sin(a)} stroke="#3B82F6" strokeWidth={1.5} strokeLinecap="round" opacity={0} style={{animation:`energyBurst 0.8s ease-out ${i*0.05}s forwards`}}/>;
                })}
                {C.powerup>=2&&C.powerup<=4&&<polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2;const r=HEX_R+(C.powerup===3?20:14); return `${CX+r*Math.cos(a)},${CY+r*Math.sin(a)}`;}).join(" ")} fill="none" stroke="#3B82F6" strokeWidth={C.powerup===3?8:4} opacity={C.powerup===3?0.3:0.15} style={{filter:"blur(4px)",transition:"all 0.3s",animation:"powerGlow 0.6s ease-in-out infinite"}}/>}
                {C.powerup>=3&&C.powerup<=4&&HEXMODS.map((m,i)=>{const next=HEXMODS[(i+1)%6];const a1=(m.a*Math.PI)/180,a2=(next.a*Math.PI)/180;const mr=54; return <line key={`ml${i}`} x1={CX+mr*Math.cos(a1)} y1={CY+mr*Math.sin(a1)} x2={CX+mr*Math.cos(a2)} y2={CY+mr*Math.sin(a2)} stroke="#3B82F6" strokeWidth={1.5} opacity={0} style={{animation:`energyBurst 0.5s ease-out ${i*0.08}s forwards`}}/>;
                })}
              </g>
            )}
            {C.hexBorder > 0 && (() => {
              const pts=Array.from({length:7},(_,j)=>{const a=(Math.PI/3)*(j%6)-Math.PI/2; return [CX+HEX_R*Math.cos(a),CY+HEX_R*Math.sin(a)];});
              const segs=pts.slice(0,-1).map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ")+"Z";
              const perim=6*2*HEX_R*Math.sin(Math.PI/6)*1.01;
              return <path d={segs.replace("Z",` L${pts[0][0]},${pts[0][1]}`)} fill="none" stroke="#3B82F6" strokeWidth={C.hexBorderGlow?5:3} opacity={C.hexBorderGlow?0.8:0.7} strokeDasharray={perim} strokeDashoffset={perim*(1-C.hexBorder)} style={C.hexBorderGlow?{filter:"drop-shadow(0 0 8px #3B82F6)",animation:"issuePulse 1.2s ease-in-out infinite"}:undefined}/>;
            })()}
            <text x={CX} y={CY - 18} fill={C.powerup>=2&&C.powerup<=4?"#1D4ED8":"#92400E"} fontSize="13" fontWeight="800" textAnchor="middle" fontFamily="system-ui" letterSpacing="0.06em">HEXAGON</text>
            {HEXMODS.map((mod, j) => {
              const modsToShow = playing ? C.hexModsRevealed : 6;
              if (j >= modsToShow) return null;
              const a = (mod.a * Math.PI) / 180, mr = 54;
              const mx = CX + mr * Math.cos(a), my = CY + mr * Math.sin(a);
              const cMod = C.mods[mod.label]; const modColor = cMod ? cMod.color : mod.color;
              const modFlash = cMod?.flash; const modSolid = cMod && !cMod.flash;
              return (
                <g key={j} style={{ animation: "modPop 0.5s ease" }}>
                  {modSolid && <circle cx={mx} cy={my} r={24} fill="none" stroke={modColor} strokeWidth={1.5} opacity={0.25} />}
                  <circle cx={mx} cy={my} r={20} fill={modSolid ? `${modColor}18` : "#fff"} stroke={modColor} strokeWidth={cMod ? 2.5 : 1.5}
                    style={modFlash ? { animation: "modFlash 0.4s ease-in-out infinite" } : undefined} />
                  <text x={mx} y={my + 3} fill={modColor} fontSize="8" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{mod.label}</text>
                </g>
              );
            })}
          </g>
        )}

        {FLOWS.map(flow => {
          const p = flowPos[flow.id]; if (!p) return null;
          const w = 138, h = 46;
          const isSel = selected === flow.id && !playing;
          const dimmed = !isVis(flow.id);
          const isAmber = playing && C.f5Amber && flow.id === "F5";
          const cnts = {}; let tot = 0;
          flow.steps.forEach(s => { cnts[s.status] = (cnts[s.status]||0)+1; tot++; });
          const okSteps = (cnts.active||0) + (cnts.external||0);
          const pctOk = tot ? Math.round(okSteps/tot*100) : 0;
          return (
            <g key={flow.id} onClick={() => clickFlow(flow.id)}
              style={{ cursor: playing ? "default" : "pointer",
                opacity: dimmed ? 0 : 1, transition: "opacity 0.6s ease",
                transform: dimmed ? "scale(0.9)" : "scale(1)", transformOrigin: `${p.x}px ${p.y}px` }}>
              {isAmber && <rect x={p.x-w/2-4} y={p.y-h/2-4} width={w+8} height={h+8} rx={14} fill="none" stroke="#F59E0B" strokeWidth={2.5} opacity={0.6} style={{animation:"issuePulse 0.8s ease-in-out infinite"}}/>}
              <rect x={p.x - w/2} y={p.y - h/2} width={w} height={h} rx={12}
                fill={isAmber ? "#FFFBEB" : isSel ? "#F8FAFC" : "#fff"}
                stroke={isAmber ? "#F59E0B" : isSel ? flow.color : "#E2E8F0"}
                strokeWidth={isSel || isAmber ? 2 : 1} filter={isSel ? "url(#shB)" : "url(#sh)"} />
              <rect x={p.x-w/2+3} y={p.y-h/2+10} width={4} height={h-20} rx={2} fill={flow.color} />
              <text x={p.x-w/2+16} y={p.y-10} fill={flow.color} fontSize="14" fontWeight="800" fontFamily="system-ui">{flow.id}</text>
              <text x={p.x-w/2+16} y={p.y+3} fill="#334155" fontSize="10" fontWeight="600" fontFamily="system-ui">
                {flow.name.length > 18 ? flow.name.slice(0,17)+"…" : flow.name}
              </text>
              <g>{(() => { let off=0; const bw=w-32; return Object.entries(SC).map(([k,cfg]) => { const c=cnts[k]||0; if(!c) return null; const sw=(c/tot)*bw; const el = <rect key={k} x={p.x-w/2+16+off} y={p.y+12} width={sw} height={3} rx={1.5} fill={cfg.border}/>; off+=sw+0.5; return el; }); })()}</g>
              <text x={p.x+w/2-10} y={p.y+15} fill="#94A3B8" fontSize="8" textAnchor="end" fontFamily="system-ui">{pctOk}%</text>
            </g>
          );
        })}

        {showDNA && isVis("F8") && (() => {
          const f8 = flowPos.F8, bx = f8.x + 5, by = f8.y + 50;
          const DNA_NODES = [
            { x:bx, y:by, w:74, label:"Issues In", sub:"133 captured", color:"#DC2626", bg:"#FEF2F2" },
            { x:bx, y:by+44, w:86, label:"Gate 1: Signal", sub:"128 stuck ⚠", color:"#DC2626", bg:"#FEF2F2", bottleneck:true },
            { x:bx, y:by+88, w:92, label:"Gate 2: Density", sub:"Balloon or Rock", color:"#F59E0B", bg:"#FFFBEB" },
            { x:bx+2, y:by+136, w:72, label:"Balloon", sub:"quick fix", color:"#22C55E", bg:"#F0FDF4" },
            { x:bx+80, y:by+136, w:72, label:"Rock", sub:"3 tracks", color:"#EF4444", bg:"#FEF2F2" },
          ];
          const ROCK_TRACKS = [
            { x:bx+50, y:by+176, w:68, label:"Policy", sub:"0 linked", color:"#8B5CF6" },
            { x:bx+122, y:by+176, w:78, label:"Operational", sub:"0 started", color:"#D97706" },
            { x:bx+85, y:by+210, w:76, label:"Capability", sub:"4 total", color:"#3B82F6" },
          ];
          const dnaCount = !playing ? 8 : C.dnaNodesRevealed;
          return (
            <g>
              {dnaCount > 0 && <text x={bx+40} y={by-10} fill="#DC2626" fontSize="8" fontWeight="700" fontFamily="system-ui" opacity={0.5} letterSpacing="0.06em" textAnchor="middle" style={{animation:"fadeIn 0.3s ease"}}>DNA TRIANGLE</text>}
              {dnaCount >= 2 && <line x1={DNA_NODES[0].x+DNA_NODES[0].w/2} y1={DNA_NODES[0].y+20} x2={DNA_NODES[1].x+DNA_NODES[1].w/2} y2={DNA_NODES[1].y-4} stroke="#94A3B8" strokeWidth={1} markerEnd="url(#arr)" opacity={0.4} style={{animation:"fadeIn 0.3s ease"}}/>}
              {dnaCount >= 3 && <line x1={DNA_NODES[1].x+DNA_NODES[1].w/2} y1={DNA_NODES[1].y+20} x2={DNA_NODES[2].x+DNA_NODES[2].w/2} y2={DNA_NODES[2].y-4} stroke="#94A3B8" strokeWidth={1} markerEnd="url(#arr)" opacity={0.4} style={{animation:"fadeIn 0.3s ease"}}/>}
              {dnaCount >= 4 && <line x1={DNA_NODES[2].x+DNA_NODES[2].w/2} y1={DNA_NODES[2].y+20} x2={DNA_NODES[3].x+DNA_NODES[3].w/2} y2={DNA_NODES[3].y-4} stroke="#22C55E" strokeWidth={1} markerEnd="url(#arr)" opacity={0.4} style={{animation:"fadeIn 0.3s ease"}}/>}
              {dnaCount >= 5 && <line x1={DNA_NODES[2].x+DNA_NODES[2].w/2} y1={DNA_NODES[2].y+20} x2={DNA_NODES[4].x+DNA_NODES[4].w/2} y2={DNA_NODES[4].y-4} stroke="#EF4444" strokeWidth={1} markerEnd="url(#arr)" opacity={0.4} style={{animation:"fadeIn 0.3s ease"}}/>}
              {ROCK_TRACKS.map((t,i) => dnaCount >= 6 + i && <line key={`rt${i}`} x1={DNA_NODES[4].x+DNA_NODES[4].w/2} y1={DNA_NODES[4].y+20} x2={t.x+t.w/2} y2={t.y-4} stroke={t.color} strokeWidth={0.8} markerEnd="url(#arr)" opacity={0.3} style={{animation:"fadeIn 0.3s ease"}}/>)}
              {DNA_NODES.map((n,i) => {
                if (i >= dnaCount) return null;
                return (
                  <g key={`dn${i}`} style={{animation:"modPop 0.4s ease"}}>
                    {n.bottleneck && <rect x={n.x-4} y={n.y-6} width={n.w+8} height={24} rx={8} fill="none" stroke="#DC2626" strokeWidth={1.5} strokeDasharray="3 2" opacity={0.5} style={{animation:"issuePulse 2s ease-in-out infinite"}}/>}
                    <rect x={n.x} y={n.y-2} width={n.w} height={20} rx={5} fill={n.bg} stroke={n.color} strokeWidth={n.bottleneck?1.5:0.8} filter="url(#sh)"/>
                    <text x={n.x+n.w/2} y={n.y+7} fill={n.color} fontSize="8" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{n.label}</text>
                    <text x={n.x+n.w/2} y={n.y+15} fill="#64748B" fontSize="6" textAnchor="middle" fontFamily="system-ui">{n.sub}</text>
                  </g>
                );
              })}
              {ROCK_TRACKS.map((t,i) => {
                if (5 + i >= dnaCount) return null;
                return (
                  <g key={`rtn${i}`} style={{animation:"modPop 0.4s ease"}}>
                    <rect x={t.x} y={t.y-2} width={t.w} height={18} rx={4} fill="#F8FAFC" stroke={t.color} strokeWidth={0.8} filter="url(#sh)"/>
                    <text x={t.x+t.w/2} y={t.y+6} fill={t.color} fontSize="7" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{t.label}</text>
                    <text x={t.x+t.w/2} y={t.y+13} fill="#94A3B8" fontSize="6" textAnchor="middle" fontFamily="system-ui">{t.sub}</text>
                  </g>
                );
              })}
              {(C.dnaFeedback || (!playing && (selected === "F1" || selected === "F8"))) && spineCount >= SPINE.length && (() => {
                const exec = spinePos.sp_execution;
                if (!exec) return null;
                const bX=DNA_NODES[3].x+DNA_NODES[3].w/2, bY=DNA_NODES[3].y+8;
                const oX=ROCK_TRACKS[1].x+ROCK_TRACKS[1].w/2, oY=ROCK_TRACKS[1].y+8;
                const bPath = makePathOff(bX,bY,exec.x,exec.y, 200);
                const [bmx,bmy] = bezMidOff(bX,bY,exec.x,exec.y, 200);
                const oPath = makePathOff(oX,oY,exec.x,exec.y, 170);
                const [omx,omy] = bezMidOff(oX,oY,exec.x,exec.y, 170);
                return (
                  <g style={{animation:"fadeIn 0.6s ease"}}>
                    <path d={bPath} fill="none" stroke="#22C55E" strokeWidth={1.8} strokeDasharray="5 3" markerEnd="url(#arrG)" opacity={0.6}/>
                    <g><rect x={bmx-24} y={bmy-8} width={48} height={14} rx={7} fill="#F0FDF4" stroke="#BBF7D0" strokeWidth={0.6}/><text x={bmx} y={bmy+2} fill="#22C55E" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">quick fix</text></g>
                    {[0,0.15].map((off,di) => <circle key={`bp${di}`} r={4-di*1.5} fill="#22C55E" opacity={0.7-di*0.2}><animateMotion dur="2s" repeatCount="indefinite" begin={`${off}s`} path={bPath} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1"/><animate attributeName="opacity" values={`0;${0.7-di*0.2};${0.7-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="2s" repeatCount="indefinite" begin={`${off}s`}/></circle>)}
                    <path d={oPath} fill="none" stroke="#D97706" strokeWidth={1.8} strokeDasharray="5 3" markerEnd="url(#arr)" opacity={0.6}/>
                    <g><rect x={omx-26} y={omy-8} width={52} height={14} rx={7} fill="#FFFBEB" stroke="#FDE68A" strokeWidth={0.6}/><text x={omx} y={omy+2} fill="#D97706" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">operational</text></g>
                    {[0.3,0.45].map((off,di) => <circle key={`op${di}`} r={4-di*1.5} fill="#D97706" opacity={0.7-di*0.2}><animateMotion dur="2s" repeatCount="indefinite" begin={`${off}s`} path={oPath} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1"/><animate attributeName="opacity" values={`0;${0.7-di*0.2};${0.7-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="2s" repeatCount="indefinite" begin={`${off}s`}/></circle>)}
                  </g>
                );
              })()}
            </g>
          );
        })()}

        {(C.capToF9 || (!playing && (selected === "F9" || selected === "F8"))) && isVis("F9") && isVis("F8") && showDNA && (() => {
          const f8 = flowPos.F8, bx = f8.x + 5, by = f8.y + 50;
          const capX = bx + 85 + 76/2, capY = by + 210 + 8;
          const f9 = flowPos.F9;
          const capPath = makePath(capX, capY, f9.x, f9.y);
          const [cmx, cmy] = bezMid(capX, capY, f9.x, f9.y);
          return (
            <g style={{ animation:"fadeIn 0.5s ease" }}>
              <path d={capPath} fill="none" stroke="#3B82F6" strokeWidth={2} strokeDasharray="5 3" markerEnd="url(#arr)" opacity={0.6} />
              <g><rect x={cmx-22} y={cmy-8} width={44} height={14} rx={7} fill="#EFF6FF" stroke="#BFDBFE" strokeWidth={0.6}/><text x={cmx} y={cmy+2} fill="#3B82F6" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">demands</text></g>
              {[0, 0.3].map((off, di) => (
                <circle key={`cap${di}`} r={4 - di * 1.5} fill="#3B82F6" opacity={0.7 - di * 0.2}>
                  <animateMotion dur="4s" repeatCount="indefinite" begin={`${off}s`} path={capPath} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                  <animate attributeName="opacity" values={`0;${0.7-di*0.2};${0.7-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="4s" repeatCount="indefinite" begin={`${off}s`} />
                </circle>
              ))}
            </g>
          );
        })()}
        {(C.f9ToHex || (!playing && selected === "F9")) && isVis("F9") && (() => {
          const f9 = flowPos.F9, hx = flowPos.hexagon;
          const hexPath = makePath(f9.x, f9.y, hx.x, hx.y);
          const [hmx, hmy] = bezMid(f9.x, f9.y, hx.x, hx.y);
          return (
            <g style={{ animation:"fadeIn 0.5s ease" }}>
              <path d={hexPath} fill="none" stroke="#2563EB" strokeWidth={2} markerEnd="url(#arr)" opacity={0.5} />
              <g><rect x={hmx-22} y={hmy-8} width={44} height={14} rx={7} fill="#EFF6FF" stroke="#BFDBFE" strokeWidth={0.6}/><text x={hmx} y={hmy+2} fill="#2563EB" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">enables</text></g>
              {[0.1, 0.4].map((off, di) => (
                <circle key={`f9h${di}`} r={4 - di * 1.5} fill="#2563EB" opacity={0.7 - di * 0.2}>
                  <animateMotion dur="1s" repeatCount="indefinite" begin={`${off}s`} path={hexPath} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                  <animate attributeName="opacity" values={`0;${0.7-di*0.2};${0.7-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="1s" repeatCount="indefinite" begin={`${off}s`} />
                </circle>
              ))}
            </g>
          );
        })()}
        {(C.f9ToOps || (!playing && selected === "F9")) && isVis("F9") && showSpine && spineCount >= SPINE.length && (() => {
          const f9 = flowPos.F9;
          const ops = spinePos.sp_operations;
          if (!ops) return null;
          const opsPath = makePathOff(f9.x, f9.y, ops.x, ops.y, 280);
          const [emx, emy] = bezMidOff(f9.x, f9.y, ops.x, ops.y, 280);
          return (
            <g style={{ animation:"fadeIn 0.5s ease" }}>
              <path d={opsPath} fill="none" stroke="#2563EB" strokeWidth={2} strokeDasharray="5 3" markerEnd="url(#arrG)" opacity={0.6} />
              <g><rect x={emx-20} y={emy-8} width={40} height={14} rx={7} fill="#EFF6FF" stroke="#BFDBFE" strokeWidth={0.6}/><text x={emx} y={emy+2} fill="#2563EB" fontSize="7" textAnchor="middle" fontFamily="system-ui" fontWeight="600">builds</text></g>
              {[0.2, 0.5].map((off, di) => (
                <circle key={`f9o${di}`} r={4 - di * 1.5} fill="#2563EB" opacity={0.7 - di * 0.2}>
                  <animateMotion dur="4s" repeatCount="indefinite" begin={`${off}s`} path={opsPath} calcMode="spline" keySplines="0.4 0 0.2 1" keyTimes="0;1" />
                  <animate attributeName="opacity" values={`0;${0.7-di*0.2};${0.7-di*0.2};0`} keyTimes="0;0.1;0.8;1" dur="4s" repeatCount="indefinite" begin={`${off}s`} />
                </circle>
              ))}
            </g>
          );
        })()}

        {!playing && <g transform="translate(24,950)">
          {Object.entries(SC).map(([k, v], i) => (
            <g key={k} transform={`translate(${i * 88},0)`}>
              <circle cx={5} cy={5} r={4} fill={v.border} /><text x={14} y={9} fill="#64748B" fontSize="9" fontFamily="system-ui" fontWeight="600">{v.label}</text>
            </g>
          ))}
        </g>}

        {playing && (C.sapFocus || C.fractalZoom || C.dimAll) && (
          <rect x={-50} y={-80} width={1200} height={1200} fill="#0F172A" opacity={C.dimAll ? 0.6 : 0.75} style={{transition:"opacity 0.6s"}} />
        )}

        {/* ========== SAP ZOOM-IN (Scene 15) ========== */}
        {playing && C.sapFocus && (() => {
          const ph = C.sapPhase;
          // 1.75x zoomed hexagon
          const zCX = 480, zCY = 340, zHR = 271, zMR = 201, modR = 34;
          const contentA = (210 * Math.PI) / 180;
          const cX = zCX + zMR * Math.cos(contentA);
          const cY = zCY + zMR * Math.sin(contentA);

          // Orbit parameters — tighten as dimensions align
          const orbitR = ph < 5 ? 62 : ph < 6 ? 48 : ph < 7 ? 32 : ph < 8 ? 18 : 0;
          const orbitDur = ph < 5 ? "3.5s" : ph < 7 ? "2.5s" : "1.5s";
          const orbitKey = ph < 5 ? "a" : ph < 7 ? "b" : "c";

          const sapDims = [
            { icon: "S", label: "Selection", desc: "What brands?", color: "#A78BFA" },
            { icon: "A", label: "Availability", desc: "When ready?", color: "#818CF8" },
            { icon: "P", label: "Price", desc: "At what cost?", color: "#C084FC" },
          ];
          const cardBaseX = cX - 170, cardBaseY = cY - 58;

          return (
            <g>
              {/* ---- Zoomed Hexagon ---- */}
              <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${zCX+zHR*Math.cos(a)},${zCY+zHR*Math.sin(a)}`;}).join(" ")}
                fill="#FFFDF5" stroke="#D4A017" strokeWidth={2.5} filter="url(#shB)" style={{animation:"fadeIn 0.8s ease"}} />
              <text x={zCX} y={zCY-40} fill="#92400E" fontSize="20" fontWeight="800" textAnchor="middle" fontFamily="system-ui" letterSpacing="0.06em" style={{animation:"fadeIn 0.8s ease"}}>HEXAGON</text>
              <text x={zCX} y={zCY-18} fill="#D4A017" fontSize="11" textAnchor="middle" fontFamily="system-ui" opacity={0.5} style={{animation:"fadeIn 1s ease"}}>marketplace</text>

              {/* Non-Content Modules */}
              {HEXMODS.map((mod,j) => {
                if (mod.label === "Content") return null;
                const a = (mod.a * Math.PI) / 180;
                const mx = zCX + zMR * Math.cos(a), my = zCY + zMR * Math.sin(a);
                return (
                  <g key={`zm${j}`} style={{animation:"fadeIn 0.6s ease"}}>
                    <circle cx={mx} cy={my} r={modR} fill="#fff" stroke={mod.color} strokeWidth={1.5} />
                    <text x={mx} y={my+4} fill={mod.color} fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{mod.label}</text>
                  </g>
                );
              })}

              {/* ---- Content Module (transforms through phases) ---- */}
              {/* Phase 0: Normal Content circle */}
              {ph === 0 && (
                <g>
                  <circle cx={cX} cy={cY} r={modR} fill="#fff" stroke="#D4A017" strokeWidth={1.5} />
                  <text x={cX} y={cY+4} fill="#D4A017" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Content</text>
                </g>
              )}

              {/* Phase 1: Content flashes red 3x via CSS keyframes */}
              {ph === 1 && (
                <g>
                  <circle cx={cX} cy={cY} r={modR+12} fill="none" stroke="#EF4444" strokeWidth={8} opacity={0}
                    style={{animation:"redGlow 2.4s ease-in-out"}} />
                  <circle cx={cX} cy={cY} r={modR} fill="#fff" stroke="#D4A017" strokeWidth={3}
                    style={{animation:"redFlashStroke 2.4s ease-in-out"}} />
                  <text x={cX} y={cY+4} fill="#D4A017" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui"
                    style={{animation:"redFlashFill 2.4s ease-in-out"}}>Content</text>
                </g>
              )}

              {/* Phase 2+: Content becomes the red need sphere */}
              {ph >= 2 && ph < 8 && (
                <g style={{animation:"fadeIn 0.4s ease"}}>
                  <circle cx={cX} cy={cY} r={modR+2} fill="url(#needGrad)" opacity={0.9}>
                    <animate attributeName="r" values="32;38;32" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <text x={cX} y={cY+6} fill="#fff" fontSize="22" fontWeight="800" textAnchor="middle" fontFamily="system-ui">{"\u2212"}</text>
                  <text x={cX} y={cY-modR-14} fill="#FDA4AF" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">SAP-need</text>
                  <text x={cX} y={cY-modR-2} fill="#94A3B8" fontSize="9" textAnchor="middle" fontFamily="system-ui">Content module</text>
                </g>
              )}

              {/* Bonded Content (phase 8) */}
              {ph >= 8 && (
                <g>
                  <circle cx={cX} cy={cY} r={modR} fill="#EFF6FF" stroke="#3B82F6" strokeWidth={3} />
                  <text x={cX} y={cY+4} fill="#3B82F6" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Content</text>
                </g>
              )}

              {/* ---- Title ---- */}
              <g style={{animation:"fadeIn 0.6s ease"}}>
                <text x={zCX} y={zCY - zHR - 30} fill="#E2E8F0" fontSize="16" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Every click hides a deeper event</text>
                {ph >= 2 && <text x={zCX} y={zCY - zHR - 10} fill="#A78BFA" fontSize="12" fontWeight="600" textAnchor="middle" fontFamily="system-ui" opacity={0.7} style={{animation:"fadeIn 0.5s ease"}}>S · A · P — Selection · Availability · Price</text>}
              </g>

              {/* ---- SAP-offer sphere (blue) — orbits around Content position ---- */}
              {ph >= 3 && ph < 8 && (
                <g key={`offer-${orbitKey}`}>
                  <g>
                    <animateMotion dur={orbitDur} repeatCount="indefinite"
                      path={`M${cX + orbitR},${cY} A${orbitR},${orbitR} 0 1,1 ${cX - orbitR},${cY} A${orbitR},${orbitR} 0 1,1 ${cX + orbitR},${cY}`}
                      calcMode="linear" />
                    <circle cx={0} cy={0} r={36} fill="url(#offerGrad)" opacity={0.85}>
                      <animate attributeName="r" values="32;38;32" dur="2s" repeatCount="indefinite" begin="0.5s"/>
                    </circle>
                    <text x={0} y={6} fill="#fff" fontSize="18" fontWeight="800" textAnchor="middle" fontFamily="system-ui">+</text>
                    <text x={0} y={-32} fill="#93C5FD" fontSize="10" fontWeight="700" textAnchor="middle" fontFamily="system-ui">SAP-offer</text>
                  </g>
                </g>
              )}

              {/* ---- Orbit ring ---- */}
              {ph >= 3 && ph < 8 && orbitR > 0 && (
                <circle cx={cX} cy={cY} r={orbitR} fill="none" stroke="#C4B5FD" strokeWidth={0.6} opacity={0.15} strokeDasharray="4 4" />
              )}

              {/* ---- SAP Dimension Cards ---- */}
              {ph >= 4 && ph < 8 && sapDims.map((dim, i) => {
                const dimPhase = 4 + i;
                if (ph < dimPhase) return null;
                const matched = ph > dimPhase;
                const cy = cardBaseY + i * 42;
                return (
                  <g key={`sd${i}`} style={{animation:"modPop 0.4s ease"}}>
                    <line x1={cardBaseX + 115} y1={cy + 17} x2={cX - modR - 6} y2={cY} stroke={matched ? "#22C55E" : dim.color} strokeWidth={0.8} strokeDasharray="3 3" opacity={matched ? 0.4 : 0.2} />
                    <rect x={cardBaseX} y={cy} width={115} height={34} rx={8}
                      fill={matched ? "#1E1B4B" : "#1E293B"} stroke={matched ? "#22C55E" : dim.color}
                      strokeWidth={matched ? 2 : 1.2} />
                    <circle cx={cardBaseX + 18} cy={cy + 17} r={12} fill={matched ? "#22C55E" : dim.color} opacity={0.9}/>
                    <text x={cardBaseX + 18} y={cy + 21} fill="#fff" fontSize="12" fontWeight="800" textAnchor="middle" fontFamily="system-ui">{matched ? "✓" : dim.icon}</text>
                    <text x={cardBaseX + 38} y={cy + 13} fill={matched ? "#86EFAC" : "#E2E8F0"} fontSize="11" fontWeight="700" fontFamily="system-ui">{dim.label}</text>
                    <text x={cardBaseX + 38} y={cy + 27} fill={matched ? "#4ADE80" : "#94A3B8"} fontSize="9" fontFamily="system-ui">{dim.desc}</text>
                  </g>
                );
              })}

              {/* ---- Phase 7: converging ---- */}
              {ph === 7 && (
                <text x={cX} y={cY + 90} fill="#C4B5FD" fontSize="12" fontWeight="600" textAnchor="middle" fontFamily="system-ui" opacity={0.6} style={{animation:"fadeIn 0.5s ease"}}>converging...</text>
              )}

              {/* ---- Phase 8: Bond flash ---- */}
              {ph >= 8 && (
                <g>
                  {/* Shockwave rings from Content */}
                  {[1,2,3].map(i => (
                    <circle key={`bsw${i}`} cx={cX} cy={cY} r={modR} fill="none" stroke="#3B82F6" strokeWidth={3 - i * 0.8}
                      style={{transformOrigin:`${cX}px ${cY}px`, opacity:0, animation:`shockwave ${1 + i * 0.3}s ease-out ${i * 0.15}s 1 forwards`}} />
                  ))}
                  {/* Bright blue flash fill on Content */}
                  <circle cx={cX} cy={cY} r={modR+4} fill="#3B82F6" opacity={0}>
                    <animate attributeName="opacity" values="0.8;0.15;0.5;0.15" dur="1.5s" fill="freeze"/>
                    <animate attributeName="r" values="20;42;36" dur="1.5s" fill="freeze"/>
                  </circle>
                  {/* Pulsing ring */}
                  <circle cx={cX} cy={cY} r={modR} fill="none" stroke="#3B82F6" strokeWidth={3} opacity={0.8}
                    style={{animation:"issuePulse 1s ease-in-out infinite"}} />
                  {/* CLICK text right next to Content */}
                  <text x={cX + modR + 18} y={cY - 8} fill="#93C5FD" fontSize="20" fontWeight="800" fontFamily="system-ui" style={{animation:"fadeIn 0.4s ease"}}>CLICK</text>
                  <text x={cX + modR + 18} y={cY + 18} fill="#94A3B8" fontSize="16" fontWeight="600" fontFamily="system-ui" style={{animation:"fadeIn 0.6s ease"}}>All three dimensions aligned.</text>
                </g>
              )}
            </g>
          );
        })()}

        {/* ========== FRACTAL ZOOM (Scene 16) ========== */}
        {playing && C.fractalZoom && (() => {
          const fph = C.fractalPhase;
          const zCX = 480, zCY = 340, zHR = 271, zMR = 201, modR = 34;
          const opsA = (90 * Math.PI) / 180;
          const opsX = zCX + zMR * Math.cos(opsA);
          const opsY = zCY + zMR * Math.sin(opsA);

          const offerOrbitR = 70, offerOrbitDur = "3.5s";

          const modLabels = ["Cons.","Capital","ERE","Ops.","GTM","Content"];
          const modAngles = [270, 330, 30, 90, 150, 210];

          // Floating hex position (phases 4-6)
          const floatCX = opsX + 240, floatCY = opsY + 80;
          const floatR = 126, floatMD = 92; // 70% of full size

          // Locked hex position (phases 7+): Consumption at 270° lands on opsX, opsY
          const miniR = 180, miniModDist = 132;
          const miniCX = opsX; // cos(270°)=0
          const miniCY = opsY + miniModDist; // sin(270°)=-1 → CY = opsY - modDist*(-1)

          // Ghost hex helper
          const ghostHex = (cx, cy, r, modDist, opa, delay, rotDeg = 0) => {
            const rotRad = (rotDeg * Math.PI) / 180;
            return (
              <g style={{animation:`fadeIn 0.8s ease ${delay}s both`}} opacity={opa}>
                <polygon points={Array.from({length:6},(_,j)=>{
                  const a = (Math.PI/3)*j - Math.PI/2 + rotRad;
                  return `${cx+r*Math.cos(a)},${cy+r*Math.sin(a)}`;
                }).join(" ")}
                  fill="none" stroke="#3B82F6" strokeWidth={1.5} />
                {modAngles.map((ang,j) => {
                  const a = (ang * Math.PI) / 180 + rotRad;
                  const mx = cx + modDist * Math.cos(a), my = cy + modDist * Math.sin(a);
                  const isCons = j === 0;
                  return <circle key={j} cx={mx} cy={my} r={r*0.12} fill={isCons ? "#475569" : "#1E40AF"} stroke={isCons ? "#94A3B8" : "#3B82F6"} strokeWidth={1} opacity={0.7}/>;
                })}
              </g>
            );
          };

          // Consumption position in floating hex
          const fConsA = (modAngles[0] * Math.PI) / 180;
          const fConsX = floatCX + floatMD * Math.cos(fConsA);
          const fConsY = floatCY + floatMD * Math.sin(fConsA);

          return (
            <g>
              {/* Zoomed Hexagon */}
              <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${zCX+zHR*Math.cos(a)},${zCY+zHR*Math.sin(a)}`;}).join(" ")}
                fill="#FFFDF5" stroke="#D4A017" strokeWidth={2.5} filter="url(#shB)" />
              <text x={zCX} y={zCY-40} fill="#92400E" fontSize="20" fontWeight="800" textAnchor="middle" fontFamily="system-ui" letterSpacing="0.06em">HEXAGON</text>

              {/* Non-Operations Modules (always visible) */}
              {HEXMODS.map((mod,j) => {
                if (mod.label === "Operations") return null;
                const a = (mod.a * Math.PI) / 180;
                const mx = zCX + zMR * Math.cos(a), my = zCY + zMR * Math.sin(a);
                return (
                  <g key={`fr${j}`}>
                    <circle cx={mx} cy={my} r={modR} fill="#fff" stroke={mod.color} strokeWidth={1.5}/>
                    <text x={mx} y={my+4} fill={mod.color} fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{mod.label}</text>
                  </g>
                );
              })}

              {/* ---- Operations Module — visible at start and after CLICK ---- */}
              {(fph === 0 || fph >= 8) && (
                <g>
                  <circle cx={opsX} cy={opsY} r={modR} fill="#F0FDFA" stroke="#0D9488" strokeWidth={2.5} />
                  <text x={opsX} y={opsY+4} fill="#0D9488" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Operations</text>
                </g>
              )}

              {/* Phase 1: Operations flashes red 3x */}
              {fph === 1 && (
                <g>
                  <circle cx={opsX} cy={opsY} r={modR+12} fill="none" stroke="#EF4444" strokeWidth={8} opacity={0}
                    style={{animation:"redGlow 2.4s ease-in-out"}} />
                  <circle cx={opsX} cy={opsY} r={modR} fill="#F0FDFA" stroke="#0D9488" strokeWidth={3}
                    style={{animation:"tealRedFlashStroke 2.4s ease-in-out"}} />
                  <text x={opsX} y={opsY+4} fill="#0D9488" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui"
                    style={{animation:"tealRedFlashFill 2.4s ease-in-out"}}>Operations</text>
                </g>
              )}

              {/* Phase 2-7: Operations as red need sphere (stays red during slide-in) */}
              {fph >= 2 && fph <= 7 && (
                <g style={fph===2?{animation:"fadeIn 0.4s ease"}:undefined}>
                  <circle cx={opsX} cy={opsY} r={modR+2} fill="url(#needGrad)" opacity={0.9}>
                    <animate attributeName="r" values="32;38;32" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <text x={opsX} y={opsY+6} fill="#fff" fontSize="22" fontWeight="800" textAnchor="middle" fontFamily="system-ui">{"\u2212"}</text>
                  {fph <= 3 && <>
                    <text x={opsX} y={opsY-modR-14} fill="#FDA4AF" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">SAP-need</text>
                    <text x={opsX} y={opsY-modR-2} fill="#94A3B8" fontSize="9" textAnchor="middle" fontFamily="system-ui">Operations module</text>
                  </>}
                  {fph === 7 && (
                    <circle cx={opsX} cy={opsY} r={modR+10} fill="none" stroke="#EF4444" strokeWidth={4} opacity={0.3} style={{animation:"issuePulse 1s ease-in-out infinite"}}/>
                  )}
                </g>
              )}

              {/* Title — changes per phase */}
              {fph <= 3 && (
                <text x={zCX} y={zCY-zHR-30} fill="#E2E8F0" fontSize="16" fontWeight="700" textAnchor="middle" fontFamily="system-ui" style={{animation:"fadeIn 0.6s ease"}}>
                  Operations has its own SAP-need
                </text>
              )}
              {fph >= 2 && fph <= 3 && <text x={zCX} y={zCY-zHR-10} fill="#5EEAD4" fontSize="12" fontWeight="600" textAnchor="middle" fontFamily="system-ui" opacity={0.7}>Who will fulfill it?</text>}
              {fph >= 4 && fph <= 6 && (
                <text x={zCX} y={zCY-zHR-30} fill="#E2E8F0" fontSize="16" fontWeight="700" textAnchor="middle" fontFamily="system-ui" style={{animation:"fadeIn 0.6s ease"}}>
                  The SAP-offer is itself a molecule
                </text>
              )}
              {fph >= 10 && (
                <text x={zCX} y={zCY-zHR-30} fill="#E2E8F0" fontSize="16" fontWeight="700" textAnchor="middle" fontFamily="system-ui" style={{animation:"fadeIn 0.6s ease"}}>
                  Every module chains. The fractal never ends.
                </text>
              )}

              {/* Phase 3: Blue offer sphere orbiting */}
              {fph === 3 && (
                <g key="foffer-orb">
                  <g>
                    <animateMotion dur={offerOrbitDur} repeatCount="indefinite"
                      path={`M${opsX + offerOrbitR},${opsY} A${offerOrbitR},${offerOrbitR} 0 1,1 ${opsX - offerOrbitR},${opsY} A${offerOrbitR},${offerOrbitR} 0 1,1 ${opsX + offerOrbitR},${opsY}`}
                      calcMode="linear" />
                    <circle cx={0} cy={0} r={36} fill="url(#offerGrad)" opacity={0.85}>
                      <animate attributeName="r" values="32;38;32" dur="2s" repeatCount="indefinite"/>
                    </circle>
                    <text x={0} y={6} fill="#fff" fontSize="18" fontWeight="800" textAnchor="middle" fontFamily="system-ui">+</text>
                    <text x={0} y={-32} fill="#93C5FD" fontSize="10" fontWeight="700" textAnchor="middle" fontFamily="system-ui">SAP-offer</text>
                  </g>
                  <circle cx={opsX} cy={opsY} r={offerOrbitR} fill="none" stroke="#93C5FD" strokeWidth={0.6} opacity={0.12} strokeDasharray="4 4" />
                </g>
              )}

              {/* Phase 4: Blue sphere settles to the right, pulses */}
              {fph === 4 && (
                <g style={{animation:"fadeIn 0.5s ease"}}>
                  <circle cx={floatCX} cy={floatCY} r={36} fill="url(#offerGrad)" opacity={0.9}>
                    <animate attributeName="r" values="32;38;32" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <text x={floatCX} y={floatCY+6} fill="#fff" fontSize="20" fontWeight="800" textAnchor="middle" fontFamily="system-ui">+</text>
                  <text x={floatCX} y={floatCY-44} fill="#93C5FD" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">SAP-offer</text>
                  <text x={floatCX} y={floatCY-30} fill="#64748B" fontSize="9" textAnchor="middle" fontFamily="system-ui">what does it contain?</text>
                  <circle cx={floatCX} cy={floatCY} r={48} fill="none" stroke="#3B82F6" strokeWidth={1.2} opacity={0.2} strokeDasharray="4 4">
                    <animate attributeName="r" values="48;54;48" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                </g>
              )}

              {/* Phase 5+: UNIFIED secondary hexagon — slides from floating to locked */}
              {fph >= 5 && fph <= 9 && (() => {
                const isFloating = fph <= 6;
                const isSliding = fph === 7;
                const isLocked = fph >= 8;
                // Consumption position in locked coords
                const consA = (modAngles[0] * Math.PI) / 180;
                const consLX = miniCX + miniModDist * Math.cos(consA);
                const consLY = miniCY + miniModDist * Math.sin(consA);

                // Style: static offset when floating, animated slide on phase 7, none when locked
                const gStyle = isFloating
                  ? { transform: 'translate(240px, -52px)' }
                  : isSliding
                    ? { animation: 'fractalSlide 2s ease-in-out forwards' }
                    : {};

                return (
                  <g key="sec-hex-unified" style={gStyle}>
                    {/* Hex outline — always drawn at LOCKED position */}
                    <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${miniCX+miniR*Math.cos(a)},${miniCY+miniR*Math.sin(a)}`;}).join(" ")}
                      fill="#0A1628" fillOpacity={0.8} stroke="#3B82F6" strokeWidth={2} style={fph===5?{animation:"fadeIn 0.6s ease"}:undefined} />

                    {/* Labels above hex */}
                    {isFloating && <>
                      <text x={miniCX} y={miniCY-miniR-16} fill="#93C5FD" fontSize="12" fontWeight="700" textAnchor="middle" fontFamily="system-ui">SAP-offer molecule</text>
                      {fph === 5 && <text x={miniCX} y={miniCY-miniR-2} fill="#64748B" fontSize="9" textAnchor="middle" fontFamily="system-ui">5 blue modules + grey Consumption</text>}
                      {fph === 6 && <text x={miniCX} y={miniCY+miniR+20} fill="#FDA4AF" fontSize="11" fontWeight="600" textAnchor="middle" fontFamily="system-ui" style={{animation:"fadeIn 0.5s ease"}}>Consumption has a need too...</text>}
                    </>}

                    {/* 6 modules */}
                    {modLabels.map((label, j) => {
                      const a = (modAngles[j] * Math.PI) / 180;
                      const mx = miniCX + miniModDist * Math.cos(a);
                      const my = miniCY + miniModDist * Math.sin(a);
                      const isCons = j === 0;

                      // After lock-in, hide Consumption (it's under Operations)
                      if (isCons && isLocked) return null;

                      // Consumption flashes grey→red during phase 6 and 7 (slide)
                      if (isCons && (fph === 6 || fph === 7)) {
                        return (
                          <g key={`fm${j}`}>
                            <circle cx={mx} cy={my} r={24} fill="none" stroke="#EF4444" strokeWidth={8} opacity={0}
                              style={{animation:"redGlow 2.4s ease-in-out"}} />
                            <circle cx={mx} cy={my} r={24} fill="#1E293B" stroke="#94A3B8" strokeWidth={2.5}
                              style={{animation:"greyRedFlashStroke 2.4s ease-in-out"}} />
                            <text x={mx} y={my+4} fill="#94A3B8" fontSize="11" fontWeight="700" textAnchor="middle" fontFamily="system-ui"
                              style={{animation:"greyRedFlashFill 2.4s ease-in-out"}}>{label}</text>
                          </g>
                        );
                      }

                      return (
                        <g key={`fm${j}`} style={fph===5?{animation:`fadeIn 0.4s ease ${0.15 + j * 0.1}s both`}:undefined}>
                          <circle cx={mx} cy={my} r={24} fill={isCons ? "#1E293B" : "#172554"} stroke={isCons ? "#94A3B8" : "#3B82F6"} strokeWidth={isCons ? 2.5 : 1.5}/>
                          <text x={mx} y={my+4} fill={isCons ? "#94A3B8" : "#93C5FD"} fontSize="11" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{label}</text>
                        </g>
                      );
                    })}

                    {/* Dashed ring showing Consumption underneath (once locked) */}
                    {isLocked && <>
                      <circle cx={consLX} cy={consLY} r={modR+6} fill="none" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.4}/>
                      <text x={consLX+modR+14} y={consLY+modR+8} fill="#94A3B8" fontSize="9" fontWeight="600" fontFamily="system-ui" opacity={0.6}>Cons. (underneath)</text>
                    </>}
                  </g>
                );
              })()}

              {/* Phase 8: CLICK — same as scene 16 */}
              {fph === 8 && (
                <g>
                  {[1,2,3].map(i => (
                    <circle key={`fbsw${i}`} cx={opsX} cy={opsY} r={modR} fill="none" stroke="#3B82F6" strokeWidth={3 - i * 0.8}
                      style={{transformOrigin:`${opsX}px ${opsY}px`, opacity:0, animation:`shockwave ${1 + i * 0.3}s ease-out ${i * 0.15}s 1 forwards`}} />
                  ))}
                  <circle cx={opsX} cy={opsY} r={modR+4} fill="#3B82F6" opacity={0}>
                    <animate attributeName="opacity" values="0.8;0.15;0.5;0.15" dur="1.5s" fill="freeze"/>
                    <animate attributeName="r" values="20;42;36" dur="1.5s" fill="freeze"/>
                  </circle>
                  <circle cx={opsX} cy={opsY} r={modR} fill="none" stroke="#3B82F6" strokeWidth={3} opacity={0.8}
                    style={{animation:"issuePulse 1s ease-in-out infinite"}} />
                  <text x={opsX + modR + 18} y={opsY - 8} fill="#93C5FD" fontSize="20" fontWeight="800" fontFamily="system-ui" style={{animation:"fadeIn 0.4s ease"}}>CLICK</text>
                  <text x={opsX + modR + 18} y={opsY + 18} fill="#94A3B8" fontSize="16" fontWeight="600" fontFamily="system-ui" style={{animation:"fadeIn 0.6s ease"}}>All three dimensions aligned.</text>
                </g>
              )}

              {/* Phase 9: Label — Ops = Consumption */}
              {fph === 9 && (
                <g style={{animation:"fadeIn 0.6s ease"}}>
                  <circle cx={opsX} cy={opsY} r={modR+8} fill="none" stroke="#EF4444" strokeWidth={2.5} opacity={0.5} style={{animation:"issuePulse 1.2s ease-in-out infinite"}}/>
                  <rect x={opsX+50} y={opsY-28} width={200} height={56} rx={10} fill="#0F172A" stroke="#EF4444" strokeWidth={0.8} opacity={0.95}/>
                  <text x={opsX+150} y={opsY-6} fill="#FDA4AF" fontSize="13" fontWeight="700" textAnchor="middle" fontFamily="system-ui">Operations (parent hex)</text>
                  <text x={opsX+150} y={opsY+16} fill="#94A3B8" fontSize="11" textAnchor="middle" fontFamily="system-ui">= Consumption (child molecule)</text>
                  <line x1={opsX+modR+8} y1={opsY} x2={opsX+50} y2={opsY} stroke="#EF4444" strokeWidth={1.5} opacity={0.5}/>
                </g>
              )}

              {/* Phase 10+: Ops tertiary chain only (ERE/GTM chains to be rebuilt) */}
              {fph >= 10 && (() => {
                const toRad = d => (d * Math.PI) / 180;

                // === OPERATIONS CHAIN ===
                const secOpsX = miniCX + miniModDist * Math.cos(toRad(90));
                const secOpsY = miniCY + miniModDist * Math.sin(toRad(90));
                const opsTertMD = 47, opsTertR = 65;
                const opsTertCX = secOpsX + opsTertMD * Math.cos(toRad(90));
                const opsTertCY = secOpsY + opsTertMD * Math.sin(toRad(90));

                return (
                  <g>
                    {/* --- Ops secondary hex stays visible --- */}
                    <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${miniCX+miniR*Math.cos(a)},${miniCY+miniR*Math.sin(a)}`;}).join(" ")}
                      fill="#0A1628" fillOpacity={0.8} stroke="#3B82F6" strokeWidth={2} />
                    {modLabels.map((label, j) => {
                      if (j === 0) return null;
                      const a = (modAngles[j] * Math.PI) / 180;
                      const mx = miniCX + miniModDist * Math.cos(a);
                      const my = miniCY + miniModDist * Math.sin(a);
                      return (
                        <g key={`fm2${j}`}>
                          <circle cx={mx} cy={my} r={24} fill="#172554" stroke="#3B82F6" strokeWidth={1.5}/>
                          <text x={mx} y={my+4} fill="#93C5FD" fontSize="11" fontWeight="700" textAnchor="middle" fontFamily="system-ui">{label}</text>
                        </g>
                      );
                    })}
                    <circle cx={opsX} cy={opsY} r={modR+6} fill="none" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 3" opacity={0.4}/>

                    {/* --- Ops tertiary ghost --- */}
                    <line x1={secOpsX} y1={secOpsY+26} x2={opsTertCX} y2={opsTertCY-opsTertR} stroke="#3B82F6" strokeWidth={1} strokeDasharray="4 3" opacity={0.25} style={{animation:"fadeIn 0.5s ease"}}/>
                    {ghostHex(opsTertCX, opsTertCY, opsTertR, opsTertMD, 0.35, 0.3, 0)}

                    {/* Ellipsis */}
                    <text x={opsTertCX} y={opsTertCY+opsTertR+18} fill="#3B82F6" fontSize="16" fontWeight="700" textAnchor="middle" fontFamily="system-ui" opacity={0.15}
                      style={{animation:"fadeIn 0.5s ease 1.2s both"}}>{"\u00B7\u00B7\u00B7"}</text>
                  </g>
                );
              })()}

              {/* Phase 11: Final label + fractal pulse */}
              {fph >= 11 && (
                <g style={{animation:"fadeIn 0.6s ease"}}>
                  <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${zCX+(zHR+14)*Math.cos(a)},${zCY+(zHR+14)*Math.sin(a)}`;}).join(" ")}
                    fill="none" stroke="#D4A017" strokeWidth={4} opacity={0.2} style={{animation:"issuePulse 1.2s ease-in-out infinite"}} />
                  <polygon points={Array.from({length:6},(_,j)=>{const a=(Math.PI/3)*j-Math.PI/2; return `${miniCX+(miniR+14)*Math.cos(a)},${miniCY+(miniR+14)*Math.sin(a)}`;}).join(" ")}
                    fill="none" stroke="#3B82F6" strokeWidth={3} opacity={0.3} style={{animation:"issuePulse 1.2s ease-in-out infinite"}} />
                  <rect x={zCX-210} y={zCY+zHR+20} width={420} height={40} rx={20} fill="#0F172A" stroke="#0D9488" strokeWidth={0.8} opacity={0.95}/>
                  <text x={zCX} y={zCY+zHR+46} fill="#E2E8F0" fontSize="15" fontWeight="700" textAnchor="middle" fontFamily="system-ui" letterSpacing="0.04em">Every module chains. Same pattern. Different scale.</text>
                </g>
              )}
            </g>
          );
        })()}
      </svg>

      {selectedFlow && !playing && (
        <div style={{ position:"absolute", right:20, top:60, width:300, maxHeight:"calc(100vh - 100px)",
          background:"rgba(255,255,255,0.97)", backdropFilter:"blur(16px)",
          border:"1px solid #E2E8F0", borderRadius:14, boxShadow:"0 8px 32px rgba(0,0,0,0.07)",
          overflowY:"auto", zIndex:20, animation:"fadeIn 0.25s ease" }}>
          <div style={{ padding:"12px 14px 10px", borderBottom:"1px solid #F1F5F9" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:15, fontWeight:800, color:selectedFlow.color }}>{selectedFlow.id}</span>
              <span style={{ fontSize:13, fontWeight:700, color:"#1E293B" }}>{selectedFlow.name}</span>
              <div data-testid="arch-livingsystem-clickable-div-1" style={{ marginLeft:"auto", cursor:"pointer", color:"#94A3B8", fontSize:18 }} onClick={() => setSelected(null)}>×</div>
            </div>
            <div style={{ fontSize:11, color:"#64748B", marginTop:4, lineHeight:1.5 }}>{selectedFlow.description}</div>
          </div>
          <div style={{ padding:"4px 8px 10px" }}>
            {selectedFlow.steps.map((step,i) => {
              const stc = SC[step.status]; const isSel = selStep?.id === step.id;
              return (
                <div data-testid="arch-livingsystem-clickable-div-2" key={step.id} onClick={() => setSelStep(p => p?.id===step.id ? null : step)} style={{
                  display:"flex", alignItems:"flex-start", gap:8, padding:"7px 6px", cursor:"pointer",
                  borderRadius:8, background:isSel?stc.bg:"transparent",
                  border:isSel?`1px solid ${stc.border}40`:"1px solid transparent", marginBottom:1 }}>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", paddingTop:2 }}>
                    <div style={{ width:10, height:10, borderRadius:"50%", background:stc.border }} />
                    {i < selectedFlow.steps.length-1 && <div style={{ width:1.5, height:14, background:"#E2E8F0", marginTop:2 }}/>}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:"#1E293B" }}>{step.label}</div>
                    <div style={{ fontSize:9, color:"#64748B", marginTop:1 }}>{step.desc}</div>
                    {isSel && <div style={{ fontSize:10, color:stc.text, marginTop:4, padding:"5px 7px", background:stc.bg, borderRadius:6, border:`1px solid ${stc.border}25`, lineHeight:1.5 }}>{step.note}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!playing && !selected && (
        <button onClick={() => setScene(0)} style={{
          position:"absolute", bottom:28, left:"50%", transform:"translateX(-50%)",
          padding:"14px 36px", borderRadius:28, border:"none",
          background:"linear-gradient(135deg,#1E293B,#334155)", boxShadow:"0 4px 20px rgba(0,0,0,0.12)",
          fontSize:15, fontWeight:700, color:"#F8FAFC", cursor:"pointer",
          display:"flex", alignItems:"center", gap:10, zIndex:10, letterSpacing:"0.02em" }}>
          Begin the Story
        </button>
      )}

      {playing && C.sceneReady && scene < SCENES.length - 1 && (() => {
        const nextSc = SCENES[scene + 1];
        const nextColor = nextSc?.color || "#3B82F6";
        return (
          <button onClick={() => setScene(p => p + 1)} style={{
            position:"absolute", bottom:32, right:28,
            padding:"16px 44px", borderRadius:32, border:"none",
            background:`linear-gradient(135deg, ${nextColor}, ${nextColor}DD)`,
            boxShadow:`0 0 30px ${nextColor}60, 0 0 60px ${nextColor}30, 0 4px 20px rgba(0,0,0,0.2)`,
            fontSize:17, fontWeight:800, color:"#fff", cursor:"pointer",
            display:"flex", alignItems:"center", gap:12, zIndex:40, letterSpacing:"0.04em",
            animation:"nextBtnAppear 0.6s ease, nextBtnPulse 2s ease-in-out 0.6s infinite",
            textShadow:"0 1px 4px rgba(0,0,0,0.3)" }}>
            <span style={{ fontSize:12, opacity:0.7 }}>{scene+2}/{SCENES.length}</span>
            {nextSc.title}
            <span style={{ fontSize:22, marginLeft:4, animation:"nextArrowBounce 1.5s ease-in-out infinite" }}>→</span>
          </button>
        );
      })()}

      {playing && C.sceneReady && scene === SCENES.length - 1 && (
        <button onClick={() => setScene(-1)} style={{
          position:"absolute", bottom:32, right:28,
          padding:"16px 44px", borderRadius:32, border:"2px solid #D4A01780",
          background:"linear-gradient(135deg, #1E293B, #0F172A)",
          boxShadow:"0 0 30px #D4A01740, 0 0 60px #D4A01720, 0 4px 20px rgba(0,0,0,0.3)",
          fontSize:17, fontWeight:800, color:"#D4A017", cursor:"pointer",
          display:"flex", alignItems:"center", gap:12, zIndex:40, letterSpacing:"0.04em",
          animation:"nextBtnAppear 0.6s ease, nextBtnPulse 2s ease-in-out 0.6s infinite" }}>
          Explore the System
          <span style={{ fontSize:22, marginLeft:4 }}>⚡</span>
        </button>
      )}

      <style>{`
        @keyframes fadeIn { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
        @keyframes drawLine { from{stroke-dashoffset:1400} to{stroke-dashoffset:0} }
        @keyframes issuePulse { 0%{opacity:0.05;stroke-width:3} 50%{opacity:0.2;stroke-width:7} 100%{opacity:0.05;stroke-width:3} }
        @keyframes modFlash { 0%{opacity:1} 40%{opacity:0.15} 60%{opacity:0.15} 100%{opacity:1} }
        @keyframes modPop { from{opacity:0;transform:scale(0.3)} to{opacity:1;transform:scale(1)} }
        @keyframes shockwave { 0%{transform:scale(1);opacity:0.5;stroke-width:3} 100%{transform:scale(2.5);opacity:0;stroke-width:0.5} }
        @keyframes energyBurst { 0%{opacity:0} 20%{opacity:0.6} 100%{opacity:0} }
        @keyframes powerGlow { 0%{opacity:0.15} 50%{opacity:0.35} 100%{opacity:0.15} }
        @keyframes redFlashStroke { 0%{stroke:#D4A017;stroke-width:2} 10%{stroke:#EF4444;stroke-width:5} 24%{stroke:#D4A017;stroke-width:2} 36%{stroke:#EF4444;stroke-width:5} 50%{stroke:#D4A017;stroke-width:2} 62%{stroke:#EF4444;stroke-width:5} 76%{stroke:#D4A017;stroke-width:2} 100%{stroke:#D4A017;stroke-width:2} }
        @keyframes redFlashFill { 0%{fill:#D4A017} 10%{fill:#EF4444} 24%{fill:#D4A017} 36%{fill:#EF4444} 50%{fill:#D4A017} 62%{fill:#EF4444} 76%{fill:#D4A017} 100%{fill:#D4A017} }
        @keyframes tealRedFlashStroke { 0%{stroke:#0D9488;stroke-width:2} 10%{stroke:#EF4444;stroke-width:5} 24%{stroke:#0D9488;stroke-width:2} 36%{stroke:#EF4444;stroke-width:5} 50%{stroke:#0D9488;stroke-width:2} 62%{stroke:#EF4444;stroke-width:5} 76%{stroke:#0D9488;stroke-width:2} 100%{stroke:#0D9488;stroke-width:2} }
        @keyframes tealRedFlashFill { 0%{fill:#0D9488} 10%{fill:#EF4444} 24%{fill:#0D9488} 36%{fill:#EF4444} 50%{fill:#0D9488} 62%{fill:#EF4444} 76%{fill:#0D9488} 100%{fill:#0D9488} }
        @keyframes redGlow { 0%{opacity:0} 10%{opacity:0.5} 24%{opacity:0} 36%{opacity:0.5} 50%{opacity:0} 62%{opacity:0.5} 76%{opacity:0} 100%{opacity:0} }
        @keyframes greyRedFlashStroke { 0%{stroke:#94A3B8;stroke-width:2} 10%{stroke:#EF4444;stroke-width:5} 24%{stroke:#94A3B8;stroke-width:2} 36%{stroke:#EF4444;stroke-width:5} 50%{stroke:#94A3B8;stroke-width:2} 62%{stroke:#EF4444;stroke-width:5} 76%{stroke:#94A3B8;stroke-width:2} 100%{stroke:#94A3B8;stroke-width:2} }
        @keyframes greyRedFlashFill { 0%{fill:#94A3B8} 10%{fill:#EF4444} 24%{fill:#94A3B8} 36%{fill:#EF4444} 50%{fill:#94A3B8} 62%{fill:#EF4444} 76%{fill:#94A3B8} 100%{fill:#94A3B8} }
        @keyframes fractalSlide { from { transform: translate(240px, -52px); } to { transform: translate(0px, 0px); } }
        @keyframes heartbeat { 0%{transform:scale(1)} 8%{transform:scale(1.03)} 12%{transform:scale(1)} 20%{transform:scale(1.025)} 24%{transform:scale(1)} 100%{transform:scale(1)} }
        @keyframes beatProgress { from{width:0} to{width:100%} }
        @keyframes nextBtnAppear { from{opacity:0;transform:translateY(20px) scale(0.9)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes nextBtnPulse { 0%{transform:scale(1);filter:brightness(1)} 50%{transform:scale(1.06);filter:brightness(1.2)} 100%{transform:scale(1);filter:brightness(1)} }
        @keyframes nextArrowBounce { 0%{transform:translateX(0)} 50%{transform:translateX(6px)} 100%{transform:translateX(0)} }
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:2px}
      `}</style>
    </div>
  );
}